/**
 * FRETBOARD GPS ENGINE V20 - Adaptive Studio Edition
 * World-class scale visualization with adaptive ear training and mastery heatmaps.
 */

class FretboardEngineV20 {
    constructor() {
        // State
        this.root = "E";
        this.scaleType = "major";
        this.numStrings = 6;
        this.displayMode = "notes";
        this.currentMode = "visualizer";
        this.currentSystem = "none"; // none, caged, 2nps, 3nps, 4nps, 212, 313, pentatonic
        this.currentPosition = "1";
        this.systemPositions = {
            '2nps': '1',
            '3nps': '1',
            '4nps': '1',
            '212': '1',
            '313': '1',
            'pentatonic': '1'
        };
        this.selectedExtensions = new Set();
        this.customIntervals = new Set();
        this.showingTriads = false;
        this.showingCAGED = false;
        this.cagedShape = "all";
        this.cagedSource = "scale"; // scale | chord
        this.cagedChordType = "maj";
        this.pentatonicFlavor = "auto"; // auto | major | minor
        this.leftyMode = false;
        this.audioEnabled = false;
        this.heatmapOverlay = true;
        this.adaptiveMode = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.toolId = 'fretboard';

        // Ear Training Game
        this.gameScore = 0;
        this.gameStreak = 0;
        this.gameAccuracyCount = 0;
        this.gameTotalNotes = 0;
        this.gameTimer = 0;
        this.gameTimerInterval = null;
        this.currentChallenge = null;

        // Interval Navigator
        this.intervalFromNote = "C";
        this.selectedInterval = null;

        // Chord Tones
        this.chordRoot = "E";
        this.chordType = "maj";

        // Tunings
        this.tunings = {
            6: ['E', 'A', 'D', 'G', 'B', 'E'],
            7: ['B', 'E', 'A', 'D', 'G', 'B', 'E'],
            8: ['F#', 'B', 'E', 'A', 'D', 'G', 'B', 'E']
        };
        this.tuningMidis = {
            6: [40, 45, 50, 55, 59, 64], // E2 A2 D3 G3 B3 E4
            7: [35, 40, 45, 50, 55, 59, 64], // B1 E2 A2 D3 G3 B3 E4
            8: [30, 35, 40, 45, 50, 55, 59, 64] // F#1 B1 E2 A2 D3 G3 B3 E4
        };
        this.tuning = this.tunings[6];

        // Audio
        this.ctx = null;
        this.analyser = null;
        this.source = null;
        this.mediaStream = null;
        this.isListening = false;
        this.isPlaying = false;

        // Notes
        this.notes = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

        // Colors — Signal x Press flagship palette per design tokens
        this.colors = {
            root:    '#f08152',   // Root: accent orange
            third:   '#f5a97a',   // 3rd: accent warm
            fifth:   '#ece2cf',   // 5th: text/ivory
            seventh: '#e0956a',   // 7th: amber
            ext:     '#c97452',   // Extensions: muted terracotta
            correct: '#a3c97a',   // Correct: muted green
            wrong:   '#e05252',   // Wrong: muted red
            surface: '#0c0b0a',
            wood:    '#16130f',
            fret:    '#9a8c78'
        };

        // CAGED Shape Boundaries (relative to Root position on 6th string)
        // Values are [minOffset, maxOffset] from the lowest root
        this.cagedBounds = {
            'E': [0, 3],
            'D': [2, 5],
            'C': [5, 8],
            'A': [7, 10],
            'G': [9, 12]
        };

        // 100+ Scale Library
        this.scales = {
            // === PENTATONICS ===
            major_pentatonic: { intervals: [0, 2, 4, 7, 9], name: 'Major Pentatonic', category: 'Pentatonic' },
            minor_pentatonic: { intervals: [0, 3, 5, 7, 10], name: 'Minor Pentatonic', category: 'Pentatonic' },
            blues: { intervals: [0, 3, 5, 6, 7, 10], name: 'Blues', category: 'Pentatonic' },
            blues_major: { intervals: [0, 2, 3, 4, 7, 9], name: 'Major Blues', category: 'Pentatonic' },
            japanese: { intervals: [0, 1, 5, 7, 8], name: 'Japanese (In Sen)', category: 'Pentatonic' },
            egyptian: { intervals: [0, 2, 5, 7, 10], name: 'Egyptian', category: 'Pentatonic' },
            chinese: { intervals: [0, 4, 6, 7, 11], name: 'Chinese', category: 'Pentatonic' },

            // === MAJOR MODES ===
            major: { intervals: [0, 2, 4, 5, 7, 9, 11], name: 'Major (Ionian)', category: 'Major Modes' },
            dorian: { intervals: [0, 2, 3, 5, 7, 9, 10], name: 'Dorian', category: 'Major Modes' },
            phrygian: { intervals: [0, 1, 3, 5, 7, 8, 10], name: 'Phrygian', category: 'Major Modes' },
            lydian: { intervals: [0, 2, 4, 6, 7, 9, 11], name: 'Lydian', category: 'Major Modes' },
            mixolydian: { intervals: [0, 2, 4, 5, 7, 9, 10], name: 'Mixolydian', category: 'Major Modes' },
            aeolian: { intervals: [0, 2, 3, 5, 7, 8, 10], name: 'Natural Minor', category: 'Major Modes' },
            locrian: { intervals: [0, 1, 3, 5, 6, 8, 10], name: 'Locrian', category: 'Major Modes' },

            // === MELODIC MINOR MODES ===
            melodic_minor: { intervals: [0, 2, 3, 5, 7, 9, 11], name: 'Melodic Minor', category: 'Melodic Minor Modes' },
            dorian_b2: { intervals: [0, 1, 3, 5, 7, 9, 10], name: 'Dorian ♭2 (Phrygian #6)', category: 'Melodic Minor Modes' },
            lydian_augmented: { intervals: [0, 2, 4, 6, 8, 9, 11], name: 'Lydian Augmented', category: 'Melodic Minor Modes' },
            lydian_dominant: { intervals: [0, 2, 4, 6, 7, 9, 10], name: 'Lydian Dominant', category: 'Melodic Minor Modes' },
            mixolydian_b6: { intervals: [0, 2, 4, 5, 7, 8, 10], name: 'Mixolydian ♭6', category: 'Melodic Minor Modes' },
            locrian_nat2: { intervals: [0, 2, 3, 5, 6, 8, 10], name: 'Locrian ♮2', category: 'Melodic Minor Modes' },
            altered: { intervals: [0, 1, 3, 4, 6, 8, 10], name: 'Altered (Super Locrian)', category: 'Melodic Minor Modes' },

            // === HARMONIC MINOR MODES ===
            harmonic_minor: { intervals: [0, 2, 3, 5, 7, 8, 11], name: 'Harmonic Minor', category: 'Harmonic Minor Modes' },
            locrian_nat6: { intervals: [0, 1, 3, 5, 6, 9, 10], name: 'Locrian ♮6', category: 'Harmonic Minor Modes' },
            ionian_sharp5: { intervals: [0, 2, 4, 5, 8, 9, 11], name: 'Ionian #5', category: 'Harmonic Minor Modes' },
            dorian_sharp4: { intervals: [0, 2, 3, 6, 7, 9, 10], name: 'Dorian #4', category: 'Harmonic Minor Modes' },
            phrygian_dominant: { intervals: [0, 1, 4, 5, 7, 8, 10], name: 'Phrygian Dominant', category: 'Harmonic Minor Modes' },
            lydian_sharp2: { intervals: [0, 3, 4, 6, 7, 9, 11], name: 'Lydian #2', category: 'Harmonic Minor Modes' },
            ultralocrian: { intervals: [0, 1, 3, 4, 6, 8, 9], name: 'Ultralocrian', category: 'Harmonic Minor Modes' },

            // === HARMONIC MAJOR MODES ===
            harmonic_major: { intervals: [0, 2, 4, 5, 7, 8, 11], name: 'Harmonic Major', category: 'Harmonic Major Modes' },
            dorian_b5: { intervals: [0, 2, 3, 5, 6, 9, 10], name: 'Dorian ♭5', category: 'Harmonic Major Modes' },
            phrygian_b4: { intervals: [0, 1, 3, 4, 7, 8, 10], name: 'Phrygian ♭4', category: 'Harmonic Major Modes' },
            lydian_b3: { intervals: [0, 2, 3, 6, 7, 9, 11], name: 'Lydian ♭3', category: 'Harmonic Major Modes' },
            mixolydian_b2: { intervals: [0, 1, 4, 5, 7, 9, 10], name: 'Mixolydian ♭2', category: 'Harmonic Major Modes' },

            // === SYMMETRIC ===
            whole_tone: { intervals: [0, 2, 4, 6, 8, 10], name: 'Whole Tone', category: 'Symmetric' },
            diminished_hw: { intervals: [0, 1, 3, 4, 6, 7, 9, 10], name: 'Diminished (H-W)', category: 'Symmetric' },
            diminished_wh: { intervals: [0, 2, 3, 5, 6, 8, 9, 11], name: 'Diminished (W-H)', category: 'Symmetric' },
            augmented: { intervals: [0, 3, 4, 7, 8, 11], name: 'Augmented', category: 'Symmetric' },
            chromatic: { intervals: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], name: 'Chromatic', category: 'Symmetric' },

            // === BEBOP ===
            bebop_dominant: { intervals: [0, 2, 4, 5, 7, 9, 10, 11], name: 'Bebop Dominant', category: 'Bebop' },
            bebop_major: { intervals: [0, 2, 4, 5, 7, 8, 9, 11], name: 'Bebop Major', category: 'Bebop' },
            bebop_minor: { intervals: [0, 2, 3, 5, 7, 8, 9, 10], name: 'Bebop Minor', category: 'Bebop' },
            bebop_dorian: { intervals: [0, 2, 3, 4, 5, 7, 9, 10], name: 'Bebop Dorian', category: 'Bebop' },

            // === EXOTIC / WORLD ===
            hungarian_minor: { intervals: [0, 2, 3, 6, 7, 8, 11], name: 'Hungarian Minor', category: 'Exotic' },
            hungarian_major: { intervals: [0, 3, 4, 6, 7, 9, 10], name: 'Hungarian Major', category: 'Exotic' },
            romanian_minor: { intervals: [0, 2, 3, 6, 7, 9, 10], name: 'Romanian Minor', category: 'Exotic' },
            spanish_gypsy: { intervals: [0, 1, 4, 5, 7, 8, 10], name: 'Spanish Gypsy', category: 'Exotic' },
            arabic: { intervals: [0, 1, 4, 5, 7, 8, 11], name: 'Arabic (Maqam Hijaz)', category: 'Exotic' },
            persian: { intervals: [0, 1, 4, 5, 6, 8, 11], name: 'Persian', category: 'Exotic' },
            byzantine: { intervals: [0, 1, 4, 5, 7, 8, 11], name: 'Byzantine', category: 'Exotic' },
            hindu: { intervals: [0, 2, 4, 5, 7, 8, 10], name: 'Hindu', category: 'Exotic' },
            hirajoshi: { intervals: [0, 2, 3, 7, 8], name: 'Hirajoshi', category: 'Exotic' },
            iwato: { intervals: [0, 1, 5, 6, 10], name: 'Iwato', category: 'Exotic' },
            kumoi: { intervals: [0, 2, 3, 7, 9], name: 'Kumoi', category: 'Exotic' },
            pelog: { intervals: [0, 1, 3, 7, 8], name: 'Pelog', category: 'Exotic' },
            balinese: { intervals: [0, 1, 3, 7, 8], name: 'Balinese', category: 'Exotic' },
            neapolitan_major: { intervals: [0, 1, 3, 5, 7, 9, 11], name: 'Neapolitan Major', category: 'Exotic' },
            neapolitan_minor: { intervals: [0, 1, 3, 5, 7, 8, 11], name: 'Neapolitan Minor', category: 'Exotic' },

            // === METAL / ROCK ===
            phrygian_dom_add9: { intervals: [0, 1, 2, 4, 5, 7, 8, 10], name: 'Phrygian Dom + ♮9', category: 'Metal' },
            lydian_b7: { intervals: [0, 2, 4, 6, 7, 9, 10], name: 'Lydian ♭7 (Dominant)', category: 'Metal' },
            double_harmonic: { intervals: [0, 1, 4, 5, 7, 8, 11], name: 'Double Harmonic', category: 'Metal' },
            enigmatic: { intervals: [0, 1, 4, 6, 8, 10, 11], name: 'Enigmatic', category: 'Metal' },
            prometheus: { intervals: [0, 2, 4, 6, 9, 10], name: 'Prometheus', category: 'Metal' },

            // === JAZZ ===
            tritone: { intervals: [0, 1, 4, 6, 7, 10], name: 'Tritone', category: 'Jazz' },
            blues_9: { intervals: [0, 2, 3, 4, 5, 7, 9, 10, 11], name: 'Blues Scale (9-tone)', category: 'Jazz' },
            half_whole_dim: { intervals: [0, 1, 3, 4, 6, 7, 9, 10], name: 'Half-Whole Diminished', category: 'Jazz' }
        };

        // Chord Types
        this.chordTypes = {
            // === TRIADS ===
            maj: { intervals: [0, 4, 7], name: 'Major' },
            min: { intervals: [0, 3, 7], name: 'Minor' },
            dim: { intervals: [0, 3, 6], name: 'Diminished' },
            aug: { intervals: [0, 4, 8], name: 'Augmented' },
            sus4: { intervals: [0, 5, 7], name: 'Sus4' },
            sus2: { intervals: [0, 2, 7], name: 'Sus2' },
            pow: { intervals: [0, 7], name: 'Power Chord (5)' },

            // === 7TH CHORDS ===
            maj7: { intervals: [0, 4, 7, 11], name: 'Major 7' },
            min7: { intervals: [0, 3, 7, 10], name: 'Minor 7' },
            dom7: { intervals: [0, 4, 7, 10], name: 'Dominant 7' },
            m7b5: { intervals: [0, 3, 6, 10], name: 'Minor 7♭5 (Half-Dim)' },
            dim7: { intervals: [0, 3, 6, 9], name: 'Diminished 7' },
            minMaj7: { intervals: [0, 3, 7, 11], name: 'Minor (Major 7)' },
            aug7: { intervals: [0, 4, 8, 10], name: 'Augmented 7' },
            maj7sharp5: { intervals: [0, 4, 8, 11], name: 'Major 7♯5' },
            dom7sus4: { intervals: [0, 5, 7, 10], name: '7sus4' },

            // === 6TH CHORDS ===
            maj6: { intervals: [0, 4, 7, 9], name: 'Major 6' },
            min6: { intervals: [0, 3, 7, 9], name: 'Minor 6' },
            maj69: { intervals: [0, 4, 7, 9, 14], name: '6/9' },

            // === 9TH CHORDS ===
            maj9: { intervals: [0, 4, 7, 11, 14], name: 'Major 9' },
            min9: { intervals: [0, 3, 7, 10, 14], name: 'Minor 9' },
            dom9: { intervals: [0, 4, 7, 10, 14], name: 'Dominant 9' },
            add9: { intervals: [0, 4, 7, 14], name: 'Add9' },
            minAdd9: { intervals: [0, 3, 7, 14], name: 'Minor Add9' },
            dom7flat9: { intervals: [0, 4, 7, 10, 13], name: '7♭9' },
            dom7sharp9: { intervals: [0, 4, 7, 10, 15], name: '7♯9 (Hendrix)' },

            // === 11TH & 13TH ===
            maj11: { intervals: [0, 4, 7, 11, 14, 17], name: 'Major 11' },
            min11: { intervals: [0, 3, 7, 10, 14, 17], name: 'Minor 11' },
            dom11: { intervals: [0, 4, 7, 10, 14, 17], name: 'Dominant 11' },
            maj13: { intervals: [0, 4, 7, 11, 14, 17, 21], name: 'Major 13' },
            min13: { intervals: [0, 3, 7, 10, 14, 17, 21], name: 'Minor 13' },
            dom13: { intervals: [0, 4, 7, 10, 14, 17, 21], name: 'Dominant 13' },

            // === ALTERED & SCARY ===
            alt: { intervals: [0, 1, 3, 4, 6, 8, 10], name: 'Altered Dominant' },
            lydomin: { intervals: [0, 4, 6, 7, 10], name: '7♯11 (Lydian Dom)' },
            dom7flat5: { intervals: [0, 4, 6, 10], name: '7♭5' },
            sus24: { intervals: [0, 2, 5, 7], name: 'Sus24' }
        };

        // Geometry - Refined for Studio Focus
        this.width = 2400;
        this.height = 420;
        this.fretCount = 24; // Full 24-fret guitar neck
        this.marginY = 55;
        this.nutX = 30;
        this.fretPositions = [];
        this.cachedPatternMaps = {};
        this.noteNodes = [];
        this.reflowUpdatePending = false;

        // Naming / notation
        this.sharpNoteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
        this.flatNoteNames = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
        this.noteSpellingMode = "auto"; // auto | sharps | flats

        // Fretboard transforms
        this.capo = 0;
        this.transposeSemitones = 0;

        // Custom system formulas
        this.customSystemFormula = '2-1-2';
        this.customSystemPattern = [2, 1, 2];
        this.systemPositions.custom = '1';
        this.customShapePresets = this.loadCustomShapePresets();

        // Constraints
        this.constraints = {
            fretSpan: 24,
            lockPosition: false,
            maxShiftPerString: 24,
            stringSkip: 'none'
        };

        // Harmonic context + voice-leading
        this.harmonicProgressionKey = 'ii_v_i_major';
        this.harmonicChordIndex = 0;
        this.harmonicDisplayMode = 'scale'; // scale | chord | targets | avoids
        this.voiceLeadingEnabled = true;
        this.harmonicTargetPitchClasses = new Set();
        this.harmonicAvoidPitchClasses = new Set();
        this.harmonicProgressions = {
            ii_v_i_major: {
                name: 'ii-V-I (Major)',
                scaleFamily: 'major',
                steps: [
                    { degree: 2, chord: 'min7', scale: 'dorian', label: 'ii' },
                    { degree: 5, chord: 'dom7', scale: 'mixolydian', label: 'V' },
                    { degree: 1, chord: 'maj7', scale: 'major', label: 'I' }
                ]
            },
            ii_v_i_minor: {
                name: 'iiø-V-i (Minor)',
                scaleFamily: 'minor',
                steps: [
                    { degree: 2, chord: 'm7b5', scale: 'locrian_nat2', label: 'iiø' },
                    { degree: 5, chord: 'dom7flat9', scale: 'phrygian_dominant', label: 'V7♭9' },
                    { degree: 1, chord: 'minMaj7', scale: 'harmonic_minor', label: 'i' }
                ]
            },
            one_five_six_four: {
                name: 'I-V-vi-IV',
                scaleFamily: 'major',
                steps: [
                    { degree: 1, chord: 'maj', scale: 'major', label: 'I' },
                    { degree: 5, chord: 'maj', scale: 'mixolydian', label: 'V' },
                    { degree: 6, chord: 'min', scale: 'aeolian', label: 'vi' },
                    { degree: 4, chord: 'maj', scale: 'lydian', label: 'IV' }
                ]
            },
            jazz_turnaround: {
                name: 'jazz turnaround',
                scaleFamily: 'major',
                steps: [
                    { degree: 1, chord: 'maj7', scale: 'major', label: 'Imaj7' },
                    { degree: 6, chord: 'dom7', scale: 'mixolydian', label: 'VI7' },
                    { degree: 2, chord: 'min7', scale: 'dorian', label: 'ii7' },
                    { degree: 5, chord: 'dom7', scale: 'mixolydian', label: 'V7' }
                ]
            }
        };

        // Arpeggio matrix
        this.arpeggioEnabled = false;
        this.arpeggioType = 'maj7';
        this.arpeggioInversion = 'root';
        this.arpeggioDrop = 'none';
        this.arpeggioStringSet = 'all';
        this.arpeggioReadout = '';

        // Practice playhead
        this.playheadBpm = 90;
        this.playheadLoopStart = 0;
        this.playheadLoopEnd = 12;
        this.playheadDirection = 'up';
        this.playheadIsRunning = false;
        this.playheadPath = [];
        this.playheadIndex = 0;
        this.playheadRepCount = 0;
        this.playheadTimer = null;
        this.lastPlayheadNode = null;

        // Live input validation
        this.validationMicEnabled = false;
        this.validationMidiEnabled = false;
        this.validationHits = 0;
        this.validationMisses = 0;
        this.validationLastNote = null;
        this.midiAccess = null;
        this.midiInputs = [];
        this.lastDetectedNote = null;
        this.lastDetectionAt = 0;

        // Workspaces
        this.workspaceA = this.loadWorkspace('A');
        this.workspaceB = this.loadWorkspace('B');
    }

    // ===== PERSISTENCE =====
    static get STORAGE_KEY() { return 'sa-tool-fretboard'; }

    saveSettings() {
        try {
            const state = {
                root: this.root,
                scaleType: this.scaleType,
                displayMode: this.displayMode,
                currentSystem: this.currentSystem,
                currentPosition: this.currentPosition,
                cagedShape: this.cagedShape,
                cagedSource: this.cagedSource,
                cagedChordType: this.cagedChordType,
                selectedExtensions: [...this.selectedExtensions],
                numStrings: this.numStrings,
                leftyMode: this.leftyMode,
                pentatonicFlavor: this.pentatonicFlavor,
                capo: this.capo,
                transposeSemitones: this.transposeSemitones,
                noteSpellingMode: this.noteSpellingMode,
                tuning: [...this.tuning]
            };
            window.flPreferenceStorage.setItem(FretboardEngineV20.STORAGE_KEY, JSON.stringify(state));
        } catch (_) {}
    }

    loadSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(FretboardEngineV20.STORAGE_KEY);
            if (!raw) return null;
            const s = JSON.parse(raw);
            if (!s || typeof s !== 'object') return null;
            return s;
        } catch {
            return null;
        }
    }

    restoreSettings() {
        const s = this.loadSettings();
        if (!s) return;
        if (s.root && this.notes.includes(s.root)) this.root = s.root;
        if (s.scaleType && this.scales[s.scaleType]) this.scaleType = s.scaleType;
        if (s.displayMode) this.displayMode = s.displayMode;
        if (s.currentSystem) this.currentSystem = s.currentSystem;
        if (s.currentPosition) this.currentPosition = s.currentPosition;
        if (s.cagedShape) this.cagedShape = s.cagedShape;
        if (s.cagedSource) this.cagedSource = s.cagedSource;
        if (s.cagedChordType && this.chordTypes[s.cagedChordType]) this.cagedChordType = s.cagedChordType;
        if (Array.isArray(s.selectedExtensions)) this.selectedExtensions = new Set(s.selectedExtensions);
        if ([6, 7, 8].includes(s.numStrings)) this.numStrings = s.numStrings;
        if (typeof s.leftyMode === 'boolean') this.leftyMode = s.leftyMode;
        if (s.pentatonicFlavor) this.pentatonicFlavor = s.pentatonicFlavor;
        if (Number.isFinite(s.capo)) this.capo = s.capo;
        if (Number.isFinite(s.transposeSemitones)) this.transposeSemitones = s.transposeSemitones;
        if (s.noteSpellingMode) this.noteSpellingMode = s.noteSpellingMode;
        if (Array.isArray(s.tuning) && s.tuning.length === this.numStrings) {
            this.tuning = s.tuning;
            this.customTuningMidis = this.buildCustomTuningMidis(s.tuning);
        } else {
            this.tuning = this.tunings[this.numStrings];
        }
    }

    applyUrlParameters() {
        if (typeof URLSearchParams !== 'function') return false;

        let params;
        try {
            params = new URLSearchParams(window.location.search);
        } catch {
            return false;
        }

        let changed = false;
        const keyParam = params.get('key');
        if (keyParam) {
            const normalizedKey = String(keyParam)
                .trim()
                .replace(/\s+/g, '')
                .replace(/♭/g, 'b')
                .replace(/♯/g, '#')
                .toLowerCase();
            const keyAliases = {
                c: 'C', 'b#': 'C',
                'c#': 'C#', db: 'C#',
                d: 'D',
                'd#': 'D#', eb: 'D#',
                e: 'E', fb: 'E',
                'e#': 'F', f: 'F',
                'f#': 'F#', gb: 'F#',
                g: 'G',
                'g#': 'G#', ab: 'G#',
                a: 'A',
                'a#': 'A#', bb: 'A#',
                b: 'B', cb: 'B'
            };
            const nextRoot = keyAliases[normalizedKey];
            if (nextRoot && this.notes.includes(nextRoot)) {
                this.root = nextRoot;
                this.chordRoot = nextRoot;
                this.intervalFromNote = nextRoot;
                changed = true;
            }
        }

        const scaleParam = params.get('scale');
        if (scaleParam) {
            const normalizeScale = (value) => String(value)
                .trim()
                .toLowerCase()
                .replace(/♭/g, 'b')
                .replace(/♯/g, 'sharp')
                .replace(/#/g, 'sharp')
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '');
            const requestedScale = normalizeScale(scaleParam);
            const scaleAliases = {
                ionian: 'major',
                major_scale: 'major',
                minor: 'aeolian',
                natural_minor: 'aeolian',
                minor_scale: 'aeolian',
                major_pent: 'major_pentatonic',
                pentatonic_major: 'major_pentatonic',
                minor_pent: 'minor_pentatonic',
                pentatonic_minor: 'minor_pentatonic',
                super_locrian: 'altered',
                phrygian_dom: 'phrygian_dominant',
                half_whole: 'diminished_hw',
                half_whole_diminished: 'diminished_hw',
                whole_half: 'diminished_wh',
                whole_half_diminished: 'diminished_wh'
            };
            let nextScale = this.scales[requestedScale] ? requestedScale : scaleAliases[requestedScale];
            if (!nextScale) {
                nextScale = Object.keys(this.scales).find((scaleKey) => normalizeScale(this.scales[scaleKey].name) === requestedScale);
            }
            if (nextScale && this.scales[nextScale]) {
                this.scaleType = nextScale;
                changed = true;
            }
        }

        if (changed) this.cachedPatternMaps = {};
        return changed;
    }

    // ===== INITIALIZATION =====
    init() {
        this.restoreSettings();
        this.applyUrlParameters();
        this.populateTuningPresets();
        this.populateSelects();
        this.renderFretboard();
        this.updateNotes();
        this.bindEvents();
        this.updateStatus();
        this.syncControlsFromState();

        // Audio cleanup on page hide
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stopPlayhead();
                this.isPlaying = false;
                if (this._playScaleTimer) { clearTimeout(this._playScaleTimer); this._playScaleTimer = null; }
                if (this.gameTimerInterval) { clearInterval(this.gameTimerInterval); this.gameTimerInterval = null; }
                if (this.isListening) this.stopListening();
                if (this.ctx && this.ctx.state === 'running') {
                    this.ctx.suspend().catch(() => {});
                }
            }
        });

        const loader = document.getElementById('FretboardLoader');
        if (loader) {
            loader.classList.add('is-loaded');
            setTimeout(() => loader.remove(), 500);
        }
    }

    // ===== TUNING PRESETS =====
    get tuningPresets() {
        return {
            'Standard (E)':    ['E', 'A', 'D', 'G', 'B', 'E'],
            'Drop D':          ['D', 'A', 'D', 'G', 'B', 'E'],
            'Open G':          ['D', 'G', 'D', 'G', 'B', 'D'],
            'Open D':          ['D', 'A', 'D', 'F#', 'A', 'D'],
            'Open E':          ['E', 'B', 'E', 'G#', 'B', 'E'],
            'DADGAD':          ['D', 'A', 'D', 'G', 'A', 'D'],
            'Half Step Down':  ['D#', 'G#', 'C#', 'F#', 'A#', 'D#'],
            'Full Step Down':  ['D', 'G', 'C', 'F', 'A', 'D'],
            'Custom':          null
        };
    }

    populateTuningPresets() {
        const select = document.getElementById('TuningPresetSelect');
        if (!select) return;
        select.innerHTML = '';
        const currentStr = this.tuning.join(' ');
        let matchFound = false;
        Object.entries(this.tuningPresets).forEach(([name, notes]) => {
            if (!notes) return; // skip Custom placeholder entry
            const opt = document.createElement('option');
            opt.value = name;
            opt.textContent = name;
            if (notes.join(' ') === currentStr) { opt.selected = true; matchFound = true; }
            select.appendChild(opt);
        });
        // Add custom option
        const customOpt = document.createElement('option');
        customOpt.value = 'Custom';
        customOpt.textContent = 'Custom';
        if (!matchFound) { customOpt.selected = true; }
        select.appendChild(customOpt);
    }

    populateSelects() {
        const rootSelect = document.getElementById('RootSelect');
        const intervalFromNote = document.getElementById('IntervalFromNote');
        const chordRootSelect = document.getElementById('ChordRootSelect');
        const cagedChordTypeSelect = document.getElementById('CAGEDChordTypeSelect');
        const arpeggioTypeSelect = document.getElementById('ArpeggioTypeSelect');
        const progressionSelect = document.getElementById('HarmonicProgressionSelect');
        const progressionInlineSelect = document.getElementById('HarmonicProgressionInlineSelect');
        const capoSelect = document.getElementById('CapoSelect');
        const transposeSelect = document.getElementById('TransposeSelect');

        const displayNoteOption = (sharpName) => {
            const idx = this.sharpNoteNames.indexOf(sharpName);
            if (idx < 0) return sharpName;
            const flatName = this.flatNoteNames[idx];
            return flatName !== sharpName ? `${sharpName}/${flatName}` : sharpName;
        };

        const fillNoteSelect = (select, value) => {
            if (!select) return;
            select.innerHTML = '';
            this.sharpNoteNames.forEach(n => {
                const opt = document.createElement('option');
                opt.value = n;
                opt.textContent = displayNoteOption(n);
                if (n === value) opt.selected = true;
                select.appendChild(opt);
            });
        };

        fillNoteSelect(rootSelect, this.root);
        fillNoteSelect(intervalFromNote, this.intervalFromNote);
        fillNoteSelect(chordRootSelect, this.chordRoot);

        const scaleSelect = document.getElementById('ScaleSelect');
        if (scaleSelect) {
            scaleSelect.innerHTML = '';
            const categories = {};
            Object.entries(this.scales).forEach(([key, scale]) => {
                if (!categories[scale.category]) categories[scale.category] = [];
                categories[scale.category].push({ key, name: scale.name });
            });

            Object.entries(categories).forEach(([catName, scales]) => {
                const optgroup = document.createElement('optgroup');
                optgroup.label = catName;
                scales.forEach(({ key, name }) => {
                    const opt = document.createElement('option');
                    opt.value = key;
                    opt.textContent = name;
                    if (key === this.scaleType) opt.selected = true;
                    optgroup.appendChild(opt);
                });
                scaleSelect.appendChild(optgroup);
            });
        }

        const fillChordTypeSelect = (select, selectedValue) => {
            if (!select) return;
            select.innerHTML = '';
            Object.entries(this.chordTypes).forEach(([key, chord]) => {
                const opt = document.createElement('option');
                opt.value = key;
                opt.textContent = chord.name;
                if (key === selectedValue) opt.selected = true;
                select.appendChild(opt);
            });
        };

        fillChordTypeSelect(cagedChordTypeSelect, this.cagedChordType);
        fillChordTypeSelect(arpeggioTypeSelect, this.arpeggioType);

        const fillProgressionSelect = (select) => {
            if (!select) return;
            select.innerHTML = '';
            Object.entries(this.harmonicProgressions).forEach(([key, progression]) => {
                const opt = document.createElement('option');
                opt.value = key;
                opt.textContent = progression.name;
                if (key === this.harmonicProgressionKey) opt.selected = true;
                select.appendChild(opt);
            });
        };

        fillProgressionSelect(progressionSelect);
        fillProgressionSelect(progressionInlineSelect);

        if (capoSelect) {
            capoSelect.innerHTML = '';
            for (let i = 0; i <= 12; i++) {
                const opt = document.createElement('option');
                opt.value = String(i);
                opt.textContent = `${i}`;
                if (i === this.capo) opt.selected = true;
                capoSelect.appendChild(opt);
            }
        }

        if (transposeSelect) {
            transposeSelect.innerHTML = '';
            for (let i = -12; i <= 12; i++) {
                const opt = document.createElement('option');
                opt.value = String(i);
                const label = i > 0 ? `+${i}` : `${i}`;
                opt.textContent = label;
                if (i === this.transposeSemitones) opt.selected = true;
                transposeSelect.appendChild(opt);
            }
        }

        this.populateCustomShapePresetSelect();
        this.populateHarmonicChordStepSelects();
        this.updateArpeggioReadout();
        this.updateHarmonicReadout();
    }

    // ===== STORAGE + NORMALIZATION HELPERS =====
    loadCustomShapePresets() {
        const base = { '2-1-2': '2-1-2', '3-1-3': '3-1-3', '1-2-1-2': '1-2-1-2' };
        try {
            const raw = window.flPreferenceStorage.getItem('ashe_fret_custom_shape_presets');
            if (!raw) return base;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return base;
            return { ...base, ...parsed };
        } catch {
            return base;
        }
    }

    saveCustomShapePresets() {
        try {
            window.flPreferenceStorage.setItem('ashe_fret_custom_shape_presets', JSON.stringify(this.customShapePresets || {}));
        } catch (_) {}
    }

    populateCustomShapePresetSelect() {
        const select = document.getElementById('CustomShapePresetSelect');
        if (!select) return;
        select.innerHTML = '';
        Object.entries(this.customShapePresets || {}).forEach(([name, formula]) => {
            const opt = document.createElement('option');
            opt.value = formula;
            opt.textContent = `${name} (${formula})`;
            select.appendChild(opt);
        });
    }

    parseCustomShapeFormula(formula) {
        if (!formula || typeof formula !== 'string') return null;
        const parts = formula
            .trim()
            .split(/[\s,\-_/]+/)
            .map(v => parseInt(v, 10))
            .filter(v => Number.isFinite(v) && v > 0 && v <= 8);
        if (!parts.length) return null;
        return parts;
    }

    setCustomSystemFormula(formula, opts = {}) {
        const parsed = this.parseCustomShapeFormula(formula);
        const status = document.getElementById('CustomShapeStatus');
        if (!parsed) {
            if (status) status.textContent = 'Invalid formula. Use values like 2-1-2 or 3-1-3.';
            return false;
        }
        this.customSystemFormula = formula.replace(/\s+/g, '');
        this.customSystemPattern = parsed;
        if (status) status.textContent = `Custom system applied: ${this.customSystemFormula}`;
        if (!opts.silent) {
            this.cachedPatternMaps = {};
            this.updateNotes();
        }
        return true;
    }

    loadWorkspace(slot) {
        try {
            const raw = window.flPreferenceStorage.getItem(`ashe_fret_workspace_${slot}`);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    saveWorkspace(slot, state) {
        try {
            window.flPreferenceStorage.setItem(`ashe_fret_workspace_${slot}`, JSON.stringify(state));
        } catch (_) {}
    }

    transposePitchClass(noteVal, semitones = this.transposeSemitones) {
        return ((noteVal + semitones) % 12 + 12) % 12;
    }

    getNoteValueFromToken(token) {
        if (!token) return null;
        const normalized = String(token).trim().toUpperCase().replace('♯', '#').replace('♭', 'B');
        const map = {
            C: 0, 'B#': 0,
            'C#': 1, DB: 1,
            D: 2,
            'D#': 3, EB: 3,
            E: 4, FB: 4,
            F: 5, 'E#': 5,
            'F#': 6, GB: 6,
            G: 7,
            'G#': 8, AB: 8,
            A: 9,
            'A#': 10, BB: 10,
            B: 11, CB: 11
        };
        return Number.isFinite(map[normalized]) ? map[normalized] : null;
    }

    getCanonicalSharpName(noteVal) {
        return this.sharpNoteNames[((noteVal % 12) + 12) % 12];
    }

    prefersFlatNaming(rootPc = this.notes.indexOf(this.root)) {
        if (this.noteSpellingMode === 'flats') return true;
        if (this.noteSpellingMode === 'sharps') return false;
        const rootName = this.getCanonicalSharpName(rootPc);
        return new Set(['F', 'A#', 'D#', 'G#']).has(rootName);
    }

    getDisplayNoteName(noteVal, rootPc = this.notes.indexOf(this.root)) {
        const idx = ((noteVal % 12) + 12) % 12;
        return this.prefersFlatNaming(rootPc) ? this.flatNoteNames[idx] : this.sharpNoteNames[idx];
    }

    // ===== HARMONIC CONTEXT =====
    getScaleFamilyDegreeOffsets(family) {
        if (family === 'minor') return [0, 2, 3, 5, 7, 8, 10];
        return [0, 2, 4, 5, 7, 9, 11];
    }

    getAvoidIntervalsForScale(scaleType, scaleIntervals) {
        const avoidMap = {
            major: [5],
            dorian: [],
            phrygian: [1],
            lydian: [],
            mixolydian: [5],
            aeolian: [1],
            locrian: [1, 6],
            harmonic_minor: [1],
            melodic_minor: [5]
        };
        const candidates = avoidMap[scaleType] || [];
        const inScale = new Set(this.normalizeIntervals(scaleIntervals));
        return candidates.filter(v => inScale.has(v));
    }

    getTargetIntervalsForChord(chordType, chordIntervals) {
        const normalized = this.normalizeIntervals(chordIntervals);
        const third = normalized.find(v => v === 3 || v === 4);
        const seventh = normalized.find(v => v === 10 || v === 11 || v === 9);
        return this.normalizeIntervals([0, third, seventh].filter(v => v !== undefined));
    }

    getCurrentHarmonicChordContext() {
        const progression = this.harmonicProgressions[this.harmonicProgressionKey] || this.harmonicProgressions.ii_v_i_major;
        const stepCount = progression.steps.length || 1;
        const index = Math.max(0, Math.min(stepCount - 1, this.harmonicChordIndex));
        const step = progression.steps[index];
        const degreeOffsets = this.getScaleFamilyDegreeOffsets(progression.scaleFamily);
        const keyRootPcBase = this.notes.indexOf(this.root);
        const degreeOffset = degreeOffsets[Math.max(0, Math.min(6, (step.degree || 1) - 1))] || 0;
        const rootPcBase = (keyRootPcBase + degreeOffset) % 12;
        const rootPc = this.transposePitchClass(rootPcBase);
        const rootName = this.getCanonicalSharpName(rootPc);
        const scaleType = this.scales[step.scale] ? step.scale : 'major';
        const scaleIntervals = this.normalizeIntervals((this.scales[scaleType] || this.scales.major).intervals);
        const chordRaw = this.getRawChordIntervals(step.chord, true);
        const chordIntervals = this.normalizeIntervals(chordRaw);
        const targetIntervals = this.getTargetIntervalsForChord(step.chord, chordRaw);
        const avoidIntervals = this.getAvoidIntervalsForScale(scaleType, scaleIntervals);

        return {
            progression,
            step,
            index,
            rootPc,
            rootName,
            scaleType,
            scaleIntervals,
            chordIntervals,
            targetIntervals,
            avoidIntervals
        };
    }

    getNextHarmonicChordContext() {
        const progression = this.harmonicProgressions[this.harmonicProgressionKey] || this.harmonicProgressions.ii_v_i_major;
        if (!progression.steps.length) return null;
        const nextIndex = (this.harmonicChordIndex + 1) % progression.steps.length;
        const prevIndex = this.harmonicChordIndex;
        this.harmonicChordIndex = nextIndex;
        const ctx = this.getCurrentHarmonicChordContext();
        this.harmonicChordIndex = prevIndex;
        return ctx;
    }

    populateHarmonicChordStepSelects() {
        const context = this.getCurrentHarmonicChordContext();
        const progression = context.progression;
        const fill = (select) => {
            if (!select) return;
            select.innerHTML = '';
            progression.steps.forEach((step, idx) => {
                const temp = this.harmonicChordIndex;
                this.harmonicChordIndex = idx;
                const c = this.getCurrentHarmonicChordContext();
                this.harmonicChordIndex = temp;
                const opt = document.createElement('option');
                opt.value = String(idx);
                opt.textContent = `${step.label} • ${this.getDisplayNoteName(c.rootPc, c.rootPc)} ${this.chordTypes[step.chord]?.name || step.chord}`;
                if (idx === context.index) opt.selected = true;
                select.appendChild(opt);
            });
        };
        fill(document.getElementById('HarmonicChordStepSelect'));
        fill(document.getElementById('HarmonicChordInlineStepSelect'));
    }

    updateHarmonicReadout() {
        const ctx = this.getCurrentHarmonicChordContext();
        const targetNames = ctx.targetIntervals.map(i => this.getDisplayNoteName((ctx.rootPc + i) % 12, ctx.rootPc)).join(' • ');
        const avoidNames = ctx.avoidIntervals.map(i => this.getDisplayNoteName((ctx.rootPc + i) % 12, ctx.rootPc)).join(' • ') || 'None';
        const content = `${ctx.step.label} in ${this.getDisplayNoteName(ctx.rootPc, ctx.rootPc)}: ${this.scales[ctx.scaleType]?.name || ctx.scaleType}. Targets: ${targetNames}. Color notes: ${avoidNames}.`;
        const drawerReadout = document.getElementById('HarmonicContextReadout');
        const voiceReadout = document.getElementById('HarmonicVoiceLeadingReadout');
        if (drawerReadout) drawerReadout.textContent = content;
        if (voiceReadout) voiceReadout.textContent = this.voiceLeadingEnabled ? 'Voice-leading enabled. Nearest target transitions are drawn on the board.' : 'Voice-leading disabled.';
    }

    // ===== ARPEGGIO MATRIX =====
    getArpeggioRawIntervals() {
        let raw = [...((this.chordTypes[this.arpeggioType] || this.chordTypes.maj7).intervals || [0, 4, 7, 11])];
        const inversion = this.arpeggioInversion === 'root' ? 0 : Math.max(0, parseInt(this.arpeggioInversion, 10) || 0);
        for (let i = 0; i < inversion && raw.length; i++) {
            const moved = raw.shift();
            raw.push(moved + 12);
        }

        if (this.arpeggioDrop === 'drop2' && raw.length >= 4) {
            raw = raw.map((v, i) => i === raw.length - 2 ? v - 12 : v);
        } else if (this.arpeggioDrop === 'drop3' && raw.length >= 4) {
            raw = raw.map((v, i) => i === raw.length - 3 ? v - 12 : v);
        }

        raw.sort((a, b) => a - b);
        return raw;
    }

    getArpeggioIntervals() {
        return this.normalizeIntervals(this.getArpeggioRawIntervals());
    }

    getAllowedArpeggioStrings() {
        if (!this.arpeggioEnabled || this.arpeggioStringSet === 'all') return null;
        const allowed = new Set();
        String(this.arpeggioStringSet).split('').forEach(ch => {
            const n = parseInt(ch, 10);
            if (!Number.isFinite(n)) return;
            const displayIdx = this.numStrings - n;
            if (displayIdx >= 0 && displayIdx < this.numStrings) allowed.add(displayIdx);
        });
        return allowed.size ? allowed : null;
    }

    updateArpeggioReadout() {
        const readout = document.getElementById('ArpeggioReadout');
        if (!readout) return;
        if (!this.arpeggioEnabled) {
            readout.textContent = 'Arpeggio mode off.';
            return;
        }
        const raw = this.getArpeggioRawIntervals();
        const rootPc = this.currentMode === 'harmonic-context'
            ? this.getCurrentHarmonicChordContext().rootPc
            : this.transposePitchClass(this.notes.indexOf(this.root));
        const notes = raw.map(i => this.getDisplayNoteName((rootPc + i) % 12, rootPc));
        readout.textContent = `${this.chordTypes[this.arpeggioType]?.name || this.arpeggioType} • ${this.arpeggioInversion} • ${this.arpeggioDrop} • ${this.arpeggioStringSet.toUpperCase()} • ${notes.join(' • ')}`;
    }

    // ===== CONSTRAINT FILTERS =====
    applyConstraintsToPatternMap(patternMap) {
        if (!patternMap) return patternMap;
        const constrained = {};
        Object.keys(patternMap).forEach(k => {
            constrained[k] = new Set([...(patternMap[k] || [])]);
        });

        const span = Math.max(3, Math.min(this.fretCount, parseInt(this.constraints.fretSpan, 10) || this.fretCount));
        if (span < this.fretCount) {
            const frets = Object.values(constrained).flatMap(set => [...set]);
            if (frets.length) {
                let minFret = Math.min(...frets);
                if (this.constraints.lockPosition && this.currentPosition !== 'all') {
                    const posIdx = Math.max(0, (parseInt(this.currentPosition, 10) || 1) - 1);
                    minFret = Math.max(0, Math.min(this.fretCount - span, posIdx * 2));
                }
                let maxFret = minFret + span;
                if (maxFret > this.fretCount) {
                    maxFret = this.fretCount;
                    minFret = Math.max(0, maxFret - span);
                }
                Object.keys(constrained).forEach(k => {
                    const original = [...constrained[k]].sort((a, b) => a - b);
                    let filtered = original.filter(f => f >= minFret && f <= maxFret);
                    if (!filtered.length && original.length) {
                        const nearest = original.reduce((best, curr) => Math.abs(curr - minFret) < Math.abs(best - minFret) ? curr : best, original[0]);
                        filtered = [nearest];
                    }
                    constrained[k] = new Set(filtered);
                });
            }
        }

        const maxShift = Math.max(0, Math.min(this.fretCount, parseInt(this.constraints.maxShiftPerString, 10) || this.fretCount));
        if (maxShift < this.fretCount) {
            let prev = null;
            for (let lowIdx = 0; lowIdx < this.numStrings; lowIdx++) {
                const s = (this.numStrings - 1) - lowIdx;
                const key = String(s);
                const values = [...(constrained[key] || [])].sort((a, b) => a - b);
                if (!values.length) continue;
                let scoped = values;
                if (prev !== null) {
                    const within = values.filter(f => Math.abs(f - prev) <= maxShift);
                    if (within.length) scoped = within;
                }
                constrained[key] = new Set(scoped);
                prev = scoped[0];
            }
        }

        return constrained;
    }

    // ===== TUNING / CAPO =====
    buildCustomTuningMidis(noteNames) {
        const baseMidis = this.tuningMidis[this.numStrings] || this.tuningMidis[6];
        return noteNames.map((note, i) => {
            const baseMidi = baseMidis[i] ?? (40 + (i * 5));
            const targetPc = this.notes.indexOf(note);
            let midi = baseMidi + ((targetPc - (baseMidi % 12) + 12) % 12);
            if (midi - baseMidi > 6) midi -= 12;
            return midi;
        });
    }

    parseCustomTuningInput(input) {
        if (!input) return null;
        const tokens = input.trim().split(/[\s,|/]+/).filter(Boolean);
        if (tokens.length !== this.numStrings) return null;
        const names = [];
        for (const token of tokens) {
            const value = this.getNoteValueFromToken(token);
            if (value === null) return null;
            names.push(this.getCanonicalSharpName(value));
        }
        return names;
    }

    applyCustomTuning(input) {
        const parsed = this.parseCustomTuningInput(input);
        const status = document.getElementById('CustomShapeStatus');
        if (!parsed) {
            if (status) status.textContent = `Invalid tuning. Provide exactly ${this.numStrings} notes (e.g. E A D G B E).`;
            return false;
        }
        this.tuning = parsed;
        this.customTuningMidis = this.buildCustomTuningMidis(parsed);
        const tuningInput = document.getElementById('CustomTuningInput');
        if (tuningInput) tuningInput.value = parsed.join(' ');
        this.cachedPatternMaps = {};
        this.renderFretboard();
        this.updateNotes();
        if (status) status.textContent = `Applied tuning: ${parsed.join(' ')}`;
        return true;
    }

    resetTuning() {
        this.tuning = this.tunings[this.numStrings];
        this.customTuningMidis = null;
        const tuningInput = document.getElementById('CustomTuningInput');
        if (tuningInput) tuningInput.value = this.tuning.join(' ');
        const status = document.getElementById('CustomShapeStatus');
        if (status) status.textContent = `Reset tuning: ${this.tuning.join(' ')}`;
        this.cachedPatternMaps = {};
        this.renderFretboard();
        this.updateNotes();
    }

    // ===== WORKSPACES =====
    collectWorkspaceState() {
        return {
            root: this.root,
            scaleType: this.scaleType,
            displayMode: this.displayMode,
            currentMode: this.currentMode,
            currentSystem: this.currentSystem,
            currentPosition: this.currentPosition,
            cagedShape: this.cagedShape,
            cagedSource: this.cagedSource,
            cagedChordType: this.cagedChordType,
            selectedExtensions: [...this.selectedExtensions],
            customIntervals: [...this.customIntervals],
            numStrings: this.numStrings,
            leftyMode: this.leftyMode,
            pentatonicFlavor: this.pentatonicFlavor,
            capo: this.capo,
            transposeSemitones: this.transposeSemitones,
            noteSpellingMode: this.noteSpellingMode,
            constraints: { ...this.constraints },
            arpeggioEnabled: this.arpeggioEnabled,
            arpeggioType: this.arpeggioType,
            arpeggioInversion: this.arpeggioInversion,
            arpeggioDrop: this.arpeggioDrop,
            arpeggioStringSet: this.arpeggioStringSet,
            harmonicProgressionKey: this.harmonicProgressionKey,
            harmonicChordIndex: this.harmonicChordIndex,
            harmonicDisplayMode: this.harmonicDisplayMode,
            voiceLeadingEnabled: this.voiceLeadingEnabled,
            customSystemFormula: this.customSystemFormula,
            customSystemPattern: [...this.customSystemPattern],
            tuning: [...this.tuning]
        };
    }

    applyWorkspaceState(state) {
        if (!state || typeof state !== 'object') return;
        this.root = state.root ?? this.root;
        this.scaleType = state.scaleType ?? this.scaleType;
        this.displayMode = state.displayMode ?? this.displayMode;
        this.currentMode = state.currentMode ?? this.currentMode;
        this.currentSystem = state.currentSystem ?? this.currentSystem;
        this.currentPosition = state.currentPosition ?? this.currentPosition;
        this.cagedShape = state.cagedShape ?? this.cagedShape;
        this.cagedSource = state.cagedSource ?? this.cagedSource;
        this.cagedChordType = state.cagedChordType ?? this.cagedChordType;
        this.selectedExtensions = new Set(state.selectedExtensions || [...this.selectedExtensions]);
        this.customIntervals = new Set(state.customIntervals || [...this.customIntervals]);
        this.numStrings = state.numStrings ?? this.numStrings;
        this.leftyMode = state.leftyMode ?? this.leftyMode;
        this.pentatonicFlavor = state.pentatonicFlavor ?? this.pentatonicFlavor;
        this.capo = state.capo ?? this.capo;
        this.transposeSemitones = state.transposeSemitones ?? this.transposeSemitones;
        this.noteSpellingMode = state.noteSpellingMode ?? this.noteSpellingMode;
        this.constraints = { ...this.constraints, ...(state.constraints || {}) };
        this.arpeggioEnabled = state.arpeggioEnabled ?? this.arpeggioEnabled;
        this.arpeggioType = state.arpeggioType ?? this.arpeggioType;
        this.arpeggioInversion = state.arpeggioInversion ?? this.arpeggioInversion;
        this.arpeggioDrop = state.arpeggioDrop ?? this.arpeggioDrop;
        this.arpeggioStringSet = state.arpeggioStringSet ?? this.arpeggioStringSet;
        this.harmonicProgressionKey = state.harmonicProgressionKey ?? this.harmonicProgressionKey;
        this.harmonicChordIndex = state.harmonicChordIndex ?? this.harmonicChordIndex;
        this.harmonicDisplayMode = state.harmonicDisplayMode ?? this.harmonicDisplayMode;
        this.voiceLeadingEnabled = state.voiceLeadingEnabled ?? this.voiceLeadingEnabled;
        this.customSystemFormula = state.customSystemFormula ?? this.customSystemFormula;
        this.customSystemPattern = Array.isArray(state.customSystemPattern) && state.customSystemPattern.length ? state.customSystemPattern : this.customSystemPattern;
        if (Array.isArray(state.tuning) && state.tuning.length === this.numStrings) {
            this.tuning = state.tuning;
            this.customTuningMidis = this.buildCustomTuningMidis(state.tuning);
        }

        this.cachedPatternMaps = {};
        this.populateSelects();
        this.syncControlsFromState();
        this.renderFretboard();
        this.showModePanel();
        this.updateNotes();
        this.updateStatus();
    }

    compareWorkspaceStates(a, b) {
        if (!a || !b) return 'Save both A and B workspaces first.';
        const keys = ['root', 'scaleType', 'currentSystem', 'currentPosition', 'currentMode', 'displayMode', 'numStrings', 'capo', 'transposeSemitones', 'harmonicProgressionKey', 'harmonicChordIndex', 'arpeggioType', 'customSystemFormula'];
        const diffs = keys.filter(k => JSON.stringify(a[k]) !== JSON.stringify(b[k])).map(k => `${k}: ${JSON.stringify(a[k])} → ${JSON.stringify(b[k])}`);
        return diffs.length ? diffs.join(' • ') : 'A and B are currently identical.';
    }

    // ===== PLAYHEAD =====
    buildPlayheadPath() {
        const start = Math.max(0, Math.min(this.fretCount, parseInt(this.playheadLoopStart, 10) || 0));
        const end = Math.max(start, Math.min(this.fretCount, parseInt(this.playheadLoopEnd, 10) || this.fretCount));
        let path = this.noteNodes
            .filter(n => n.inSystem && !n.dimmed && n.fret >= start && n.fret <= end)
            .sort((a, b) => (a.fret - b.fret) || (b.string - a.string));

        if (this.constraints.stringSkip === 'none-skip') {
            path = path.filter((n, i) => i === 0 || Math.abs(n.string - path[i - 1].string) <= 1);
        } else if (this.constraints.stringSkip === 'max1') {
            path = path.filter((n, i) => i === 0 || Math.abs(n.string - path[i - 1].string) <= 2);
        }

        if (this.playheadDirection === 'down') path.reverse();
        return path;
    }

    startPlayhead() {
        this.stopPlayhead();
        // AudioContext must be created/resumed here (user gesture) before scheduling.
        this.initAudio();
        this.playheadPath = this.buildPlayheadPath();
        if (!this.playheadPath.length) {
            const readout = document.getElementById('PlayheadStatusReadout');
            if (readout) {
                let textNode = readout.firstChild;
                if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
                    textNode = document.createTextNode('');
                    readout.insertBefore(textNode, readout.firstChild);
                }
                textNode.nodeValue = 'No notes in loop region for current view.';
            }
            return;
        }
        this.playheadIsRunning = true;
        document.getElementById('PlayheadStartBtn')?.classList.add('active');
        document.getElementById('PlayheadStopBtn')?.classList.remove('active');
        this.playheadIndex = 0;
        this.playheadRepCount = 0;
        // Web Audio lookahead scheduling — never bare setInterval for musical timing.
        const beatSec = 60 / Math.max(30, parseInt(this.playheadBpm, 10) || 90);
        this._playheadNextBeatTime = this.ctx.currentTime + 0.05;
        this._playheadBeatSec = beatSec;
        this._runPlayheadScheduler();
    }

    _runPlayheadScheduler() {
        if (!this.playheadIsRunning || !this.ctx) return;
        const lookahead = 0.12; // seconds to schedule ahead
        // _scheduledBeats: Map<uniqueKey, {node, scheduledAt}> — never mutates node objects,
        // so stale timestamps from prior loops cannot corrupt the visual highlight.
        if (!this._scheduledBeats) this._scheduledBeats = new Map();
        while (this._playheadNextBeatTime < this.ctx.currentTime + lookahead) {
            const node = this.playheadPath[this.playheadIndex];
            if (node) {
                this._scheduleNote(node.noteVal, 4 - Math.floor(node.string / 2), this._playheadNextBeatTime);
                // Unique key = loop count * pathLength + position — avoids reusing old keys.
                const key = this.playheadRepCount * this.playheadPath.length + this.playheadIndex;
                this._scheduledBeats.set(key, { node, scheduledAt: this._playheadNextBeatTime });
            }
            this._playheadNextBeatTime += this._playheadBeatSec;
            this.playheadIndex++;
            if (this.playheadIndex >= this.playheadPath.length) {
                this.playheadIndex = 0;
                this.playheadRepCount++;
                // Prune entries older than one full loop to keep the Map from growing unbounded.
                const pruneThreshold = (this.playheadRepCount - 1) * this.playheadPath.length;
                for (const k of this._scheduledBeats.keys()) {
                    if (k < pruneThreshold) this._scheduledBeats.delete(k);
                }
            }
        }
        // Visual update driven by rAF (not for timing — only for DOM highlight)
        this.playheadTimer = requestAnimationFrame(() => {
            this._updatePlayheadVisual();
            this._runPlayheadScheduler();
        });
    }

    _updatePlayheadVisual() {
        if (!this.ctx) return;
        const now = this.ctx.currentTime;
        // Find the note whose scheduled time is closest in the past (just fired).
        // Uses _scheduledBeats Map — each entry is unique per beat event, so loop
        // repeats never overwrite or corrupt earlier timestamps.
        let closest = null;
        let closestDelta = Infinity;
        if (this._scheduledBeats) {
            for (const { node, scheduledAt } of this._scheduledBeats.values()) {
                const delta = now - scheduledAt;
                if (delta >= 0 && delta < closestDelta) {
                    closestDelta = delta;
                    closest = node;
                }
            }
        }
        if (closest && closest !== this.lastPlayheadNode) {
            if (this.lastPlayheadNode?.group) this.lastPlayheadNode.group.classList.remove('is-playhead');
            if (closest.group) closest.group.classList.add('is-playhead');
            this.lastPlayheadNode = closest;
            // Update readout text without destroying the child <span id="PlayheadRepCount">.
            // Write the prefix text into a leading text node and keep the span intact.
            const readout = document.getElementById('PlayheadStatusReadout');
            if (readout) {
                // Ensure the first child is a text node (not the span).
                let textNode = readout.firstChild;
                if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
                    textNode = document.createTextNode('');
                    readout.insertBefore(textNode, readout.firstChild);
                }
                textNode.nodeValue = `Running ${this.playheadBpm} BPM • ${this.getDisplayNoteName(closest.noteVal, this.lastRenderedRootIdx)} • Fret ${closest.fret} • Reps: `;
            }
            const rep = document.getElementById('PlayheadRepCount');
            if (rep) rep.textContent = String(this.playheadRepCount);
        }
    }

    stopPlayhead() {
        this.playheadIsRunning = false;
        if (this.playheadTimer) cancelAnimationFrame(this.playheadTimer);
        this.playheadTimer = null;
        if (this.lastPlayheadNode?.group) this.lastPlayheadNode.group.classList.remove('is-playhead');
        this.lastPlayheadNode = null;
        // Clear scheduled beat map so stale highlights don't linger on next start.
        if (this._scheduledBeats) this._scheduledBeats.clear();
        document.getElementById('PlayheadStartBtn')?.classList.remove('active');
        document.getElementById('PlayheadStopBtn')?.classList.add('active');
        // Restore readout text without destroying the child <span id="PlayheadRepCount">.
        const readout = document.getElementById('PlayheadStatusReadout');
        if (readout) {
            let textNode = readout.firstChild;
            if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
                textNode = document.createTextNode('');
                readout.insertBefore(textNode, readout.firstChild);
            }
            textNode.nodeValue = 'Ready. Reps: ';
            const rep = document.getElementById('PlayheadRepCount');
            if (rep) rep.textContent = String(this.playheadRepCount);
        }
    }

    // tickPlayhead kept as a no-op stub so any external refs don't throw
    tickPlayhead() {}

    // ===== LIVE INPUT VALIDATION =====
    validateInputNote(noteVal, source = 'input') {
        if (!Number.isFinite(noteVal)) return;
        const active = new Set(this.noteNodes.filter(n => n.inSystem && !n.dimmed).map(n => n.noteVal));
        const hit = active.has(noteVal);
        if (hit) this.validationHits++;
        else this.validationMisses++;

        const noteName = this.getDisplayNoteName(noteVal, this.lastRenderedRootIdx);
        const readout = document.getElementById('ValidationReadout');
        if (readout) readout.textContent = `${source.toUpperCase()}: ${noteName} • ${hit ? 'MATCH' : 'MISS'}`;
        const hits = document.getElementById('ValidationHitsCount');
        const misses = document.getElementById('ValidationMissesCount');
        if (hits) hits.textContent = String(this.validationHits);
        if (misses) misses.textContent = String(this.validationMisses);
    }

    autoCorrelate(buffer, sampleRate) {
        const size = buffer.length;
        let rms = 0;
        for (let i = 0; i < size; i++) rms += buffer[i] * buffer[i];
        rms = Math.sqrt(rms / size);
        if (rms < 0.01) return -1;

        let r1 = 0;
        let r2 = size - 1;
        const threshold = 0.2;
        for (let i = 0; i < size / 2; i++) {
            if (Math.abs(buffer[i]) < threshold) { r1 = i; break; }
        }
        for (let i = 1; i < size / 2; i++) {
            if (Math.abs(buffer[size - i]) < threshold) { r2 = size - i; break; }
        }

        const trimmed = buffer.slice(r1, r2);
        const trimmedSize = trimmed.length;
        const c = new Array(trimmedSize).fill(0);
        for (let i = 0; i < trimmedSize; i++) {
            for (let j = 0; j < trimmedSize - i; j++) c[i] += trimmed[j] * trimmed[j + i];
        }

        let d = 0;
        while (c[d] > c[d + 1]) d++;
        let maxVal = -1;
        let maxPos = -1;
        for (let i = d; i < trimmedSize; i++) {
            if (c[i] > maxVal) {
                maxVal = c[i];
                maxPos = i;
            }
        }
        if (maxPos <= 0) return -1;
        return sampleRate / maxPos;
    }

    async startMidiValidation() {
        if (!navigator.requestMIDIAccess) {
            const readout = document.getElementById('ValidationReadout');
            if (readout) readout.textContent = 'MIDI not supported in this browser.';
            return false;
        }
        try {
            this.midiAccess = await navigator.requestMIDIAccess();
            this.midiInputs = [...this.midiAccess.inputs.values()];
            this.midiInputs.forEach(input => {
                input.onmidimessage = (e) => this.handleMidiMessage(e);
            });
            this.validationMidiEnabled = true;
            const readout = document.getElementById('ValidationReadout');
            if (readout) readout.textContent = this.midiInputs.length ? 'MIDI validation active.' : 'No MIDI inputs detected.';
            return true;
        } catch {
            const readout = document.getElementById('ValidationReadout');
            if (readout) readout.textContent = 'MIDI permission denied.';
            return false;
        }
    }

    stopMidiValidation() {
        this.validationMidiEnabled = false;
        this.midiInputs.forEach(input => { input.onmidimessage = null; });
        const readout = document.getElementById('ValidationReadout');
        if (readout) readout.textContent = 'MIDI validation stopped.';
    }

    handleMidiMessage(event) {
        if (!this.validationMidiEnabled) return;
        const [status, data1, data2] = event.data || [];
        const isNoteOn = (status & 0xf0) === 0x90 && data2 > 0;
        if (!isNoteOn) return;
        this.validateInputNote(((data1 % 12) + 12) % 12, 'midi');
    }

    syncControlsFromState() {
        const setValue = (id, value) => {
            const el = document.getElementById(id);
            if (el && value !== undefined && value !== null) el.value = String(value);
        };
        setValue('RootSelect', this.root);
        setValue('ScaleSelect', this.scaleType);
        setValue('SystemSelect', this.currentSystem);
        setValue('SystemPositionSelect', this.currentPosition);
        setValue('CAGEDShapeSelect', this.cagedShape);
        setValue('CAGEDSourceSelect', this.cagedSource);
        setValue('CAGEDChordTypeSelect', this.cagedChordType);
        setValue('PentatonicFlavorSelect', this.pentatonicFlavor);
        setValue('ArpeggioTypeSelect', this.arpeggioType);
        setValue('ArpeggioInversionSelect', this.arpeggioInversion);
        setValue('ArpeggioDropSelect', this.arpeggioDrop);
        setValue('ArpeggioStringSetSelect', this.arpeggioStringSet);
        setValue('CapoSelect', this.capo);
        setValue('TransposeSelect', this.transposeSemitones);
        setValue('NoteSpellingSelect', this.noteSpellingMode);
        setValue('ConstraintFretSpanInput', this.constraints.fretSpan);
        setValue('ConstraintMaxShiftInput', this.constraints.maxShiftPerString);
        setValue('ConstraintStringSkipSelect', this.constraints.stringSkip);
        setValue('HarmonicProgressionSelect', this.harmonicProgressionKey);
        setValue('HarmonicProgressionInlineSelect', this.harmonicProgressionKey);
        setValue('HarmonicDisplayModeSelect', this.harmonicDisplayMode);
        setValue('CustomShapeFormulaInput', this.customSystemFormula);
        const tuningInput = document.getElementById('CustomTuningInput');
        if (tuningInput) tuningInput.value = this.tuning.join(' ');
        document.querySelectorAll('[data-strings]').forEach(btn => {
            const active = parseInt(btn.dataset.strings, 10) === this.numStrings;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', String(active));
        });
        document.querySelectorAll('[data-display]').forEach(btn => {
            const active = btn.dataset.display === this.displayMode;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        document.querySelectorAll('[data-system]').forEach(btn => {
            const active = btn.dataset.system === this.currentSystem;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', String(active));
        });
        document.querySelectorAll('[data-pos]').forEach(btn => {
            const active = btn.dataset.pos === this.currentPosition;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', String(active));
        });
        document.querySelectorAll('.ext-toggle').forEach(btn => {
            const active = this.selectedExtensions.has(btn.dataset.ext);
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', String(active));
        });
        this.updateCustomIntervalButtons();
        this.populateHarmonicChordStepSelects();
        this.updateArpeggioReadout();
        this.updateHarmonicReadout();

        const setToggle = (id, isOn, labels = ['OFF', 'ON']) => {
            const btn = document.getElementById(id);
            if (!btn) return;
            btn.classList.toggle('active', !!isOn);
            btn.textContent = isOn ? labels[1] : labels[0];
            btn.setAttribute('aria-pressed', String(!!isOn));
        };
        setToggle('VoiceLeadingToggleBtn', this.voiceLeadingEnabled);
        setToggle('ArpeggioEnableBtn', this.arpeggioEnabled);
        setToggle('ConstraintPositionLockBtn', this.constraints.lockPosition);
        setToggle('ValidationMicToggleBtn', this.validationMicEnabled);
        setToggle('ValidationMidiToggleBtn', this.validationMidiEnabled);
    }

    getScaleIntervals() {
        const scaleData = this.scales[this.scaleType] || this.scales.major;
        return this.normalizeIntervals(scaleData.intervals || [0, 2, 4, 5, 7, 9, 11]);
    }

    normalizeIntervals(intervals) {
        const out = [];
        const seen = new Set();
        (intervals || []).forEach(i => {
            const normalized = ((i % 12) + 12) % 12;
            if (!seen.has(normalized)) {
                seen.add(normalized);
                out.push(normalized);
            }
        });
        return out;
    }

    getExtensionAdditions(referenceIntervals = []) {
        const additions = [];
        const refSet = new Set(this.normalizeIntervals(referenceIntervals));
        const candidates = {
            '7': [10, 11],
            '9': [2, 1],
            '11': [5, 6],
            '13': [9, 8]
        };

        this.selectedExtensions.forEach(ext => {
            const opts = candidates[ext] || [];
            const preferred = opts.find(i => refSet.has(i));
            if (preferred !== undefined) additions.push(preferred);
            else if (opts.length) additions.push(opts[0]);
        });

        return this.normalizeIntervals(additions);
    }

    getRawChordIntervals(chordType, includeExtensions = true) {
        const baseChord = this.chordTypes[chordType] || this.chordTypes.maj;
        const intervals = [...(baseChord.intervals || [])];

        if (includeExtensions && this.selectedExtensions.size > 0) {
            this.selectedExtensions.forEach(ext => {
                if (ext === '7') {
                    if (intervals.includes(11)) intervals.push(11);
                    else if (intervals.includes(9)) intervals.push(9); // dim7 context
                    else intervals.push(10);
                }
                if (ext === '9') intervals.push(14);
                if (ext === '11') intervals.push(17);
                if (ext === '13') intervals.push(21);
            });
        }

        const seen = new Set();
        return intervals.filter(i => {
            if (seen.has(i)) return false;
            seen.add(i);
            return true;
        });
    }

    getChordIntervals(chordType, includeExtensions = true) {
        return this.normalizeIntervals(this.getRawChordIntervals(chordType, includeExtensions));
    }

    getPentatonicIntervalsFromContext(scaleIntervals) {
        const normalized = this.normalizeIntervals(scaleIntervals);
        const majorPent = [0, 2, 4, 7, 9];
        const minorPent = [0, 3, 5, 7, 10];

        const isMinorish = normalized.includes(3) && !normalized.includes(4);
        let target = majorPent;

        if (this.pentatonicFlavor === 'minor') target = minorPent;
        else if (this.pentatonicFlavor === 'major') target = majorPent;
        else target = isMinorish ? minorPent : majorPent;

        const filtered = target.filter(i => normalized.includes(i));
        return filtered.length >= 4 ? filtered : target;
    }

    getIntervalsForCurrentView() {
        if (this.currentMode === 'chord-tones') {
            const chordIntervals = this.getChordIntervals(this.chordType, true);
            return this.applyCustomIntervalAdditions(chordIntervals);
        }

        if (this.currentMode === 'interval' && this.selectedInterval !== null) {
            return this.applyCustomIntervalAdditions([0, this.selectedInterval]);
        }

        const scaleIntervals = this.getScaleIntervals();
        let baseIntervals = [...scaleIntervals];

        if (this.currentSystem === 'caged' && this.cagedSource === 'chord') {
            baseIntervals = this.getChordIntervals(this.cagedChordType, true);
        } else if (this.currentSystem === 'pentatonic') {
            const pent = this.getPentatonicIntervalsFromContext(scaleIntervals);
            const extAdds = this.getExtensionAdditions(pent);
            baseIntervals = this.normalizeIntervals([...pent, ...extAdds]);
        } else {
            const extensionAdds = this.getExtensionAdditions(scaleIntervals);
            baseIntervals = this.normalizeIntervals([...scaleIntervals, ...extensionAdds]);
        }

        return this.applyCustomIntervalAdditions(baseIntervals);
    }

    applyCustomIntervalAdditions(intervals) {
        if (!this.customIntervals.size) return this.normalizeIntervals(intervals);
        return this.normalizeIntervals([...intervals, ...Array.from(this.customIntervals)]);
    }

    getOpenStringMidisLowToHigh() {
        if (Array.isArray(this.customTuningMidis) && this.customTuningMidis.length === this.numStrings) {
            return this.customTuningMidis;
        }
        return this.tuningMidis[this.numStrings] || this.tuningMidis[6];
    }

    buildNpsPatternMap(notesPerString, intervals, position, rootNote = this.root) {
        if (position === 'all') return null;

        const scaleIntervals = this.normalizeIntervals(intervals);
        if (!scaleIntervals.length) return null;

        const rootVal = this.notes.indexOf(rootNote);
        const scalePitchClasses = scaleIntervals.map(i => (rootVal + i) % 12);
        const pitchClassSet = new Set(scalePitchClasses);

        const openPitchClassesLowToHigh = this.tuning.map(n => this.notes.indexOf(n));
        const openMidisLowToHigh = this.getOpenStringMidisLowToHigh();
        const posCount = Math.max(1, scaleIntervals.length);
        const posIndex = Math.max(0, ((parseInt(position, 10) || 1) - 1) % posCount);
        const startPitchClass = scalePitchClasses[posIndex];

        const lowStringOpenPc = openPitchClassesLowToHigh[0];
        const anchorFret = ((startPitchClass - lowStringOpenPc) + 12) % 12;

        let previousPitch = openMidisLowToHigh[0] + anchorFret - 1;
        const result = {};

        for (let lowStringIndex = 0; lowStringIndex < this.numStrings; lowStringIndex++) {
            const openPc = openPitchClassesLowToHigh[lowStringIndex];
            const openMidi = openMidisLowToHigh[lowStringIndex];
            const candidateFrets = [];

            for (let fret = 0; fret <= this.fretCount; fret++) {
                const pc = (openPc + fret) % 12;
                if (pitchClassSet.has(pc)) candidateFrets.push(fret);
            }

            if (!candidateFrets.length) continue;

            let startIndex = candidateFrets.findIndex(f => (openMidi + f) >= previousPitch);
            if (startIndex < 0) startIndex = Math.max(0, candidateFrets.length - notesPerString);

            let chosen = candidateFrets.slice(startIndex, startIndex + notesPerString);
            if (!chosen.length) chosen = [candidateFrets[0]];

            if (chosen.length < notesPerString && candidateFrets.length >= notesPerString) {
                startIndex = Math.max(0, startIndex - (notesPerString - chosen.length));
                chosen = candidateFrets.slice(startIndex, startIndex + notesPerString);
            }

            const displayStringIndex = (this.numStrings - 1) - lowStringIndex;
            result[displayStringIndex] = new Set();

            chosen.forEach(baseFret => {
                [-24, -12, 0, 12, 24].forEach(shift => {
                    const f = baseFret + shift;
                    if (f >= 0 && f <= this.fretCount) result[displayStringIndex].add(f);
                });
            });

            previousPitch = openMidi + chosen[chosen.length - 1];
        }

        return result;
    }

    buildGroupedPatternMap(groupPattern, intervals, position, rootNote = this.root) {
        // Supports mixed NPS structures such as 2-1-2 and 3-1-3 across the string set.
        if (position === 'all') return null;

        const scaleIntervals = this.normalizeIntervals(intervals);
        if (!scaleIntervals.length) return null;

        const rootVal = this.notes.indexOf(rootNote);
        const scalePitchClasses = scaleIntervals.map(i => (rootVal + i) % 12);
        const pitchClassSet = new Set(scalePitchClasses);

        const openPitchClassesLowToHigh = this.tuning.map(n => this.notes.indexOf(n));
        const openMidisLowToHigh = this.getOpenStringMidisLowToHigh();
        const posCount = Math.max(1, scaleIntervals.length);
        const posIndex = Math.max(0, ((parseInt(position, 10) || 1) - 1) % posCount);
        const startPitchClass = scalePitchClasses[posIndex];

        const lowStringOpenPc = openPitchClassesLowToHigh[0];
        const anchorFret = ((startPitchClass - lowStringOpenPc) + 12) % 12;

        let previousPitch = openMidisLowToHigh[0] + anchorFret - 1;
        const result = {};

        for (let lowStringIndex = 0; lowStringIndex < this.numStrings; lowStringIndex++) {
            const notesPerString = Math.max(1, groupPattern[lowStringIndex % groupPattern.length] || groupPattern[0] || 1);
            const openPc = openPitchClassesLowToHigh[lowStringIndex];
            const openMidi = openMidisLowToHigh[lowStringIndex];
            const candidateFrets = [];

            for (let fret = 0; fret <= this.fretCount; fret++) {
                const pc = (openPc + fret) % 12;
                if (pitchClassSet.has(pc)) candidateFrets.push(fret);
            }

            if (!candidateFrets.length) continue;

            let startIndex = candidateFrets.findIndex(f => (openMidi + f) >= previousPitch);
            if (startIndex < 0) startIndex = Math.max(0, candidateFrets.length - notesPerString);

            let chosen = candidateFrets.slice(startIndex, startIndex + notesPerString);
            if (!chosen.length) chosen = [candidateFrets[0]];

            if (chosen.length < notesPerString && candidateFrets.length >= notesPerString) {
                startIndex = Math.max(0, startIndex - (notesPerString - chosen.length));
                chosen = candidateFrets.slice(startIndex, startIndex + notesPerString);
            }

            const displayStringIndex = (this.numStrings - 1) - lowStringIndex;
            result[displayStringIndex] = new Set();

            chosen.forEach(baseFret => {
                [-24, -12, 0, 12, 24].forEach(shift => {
                    const f = baseFret + shift;
                    if (f >= 0 && f <= this.fretCount) result[displayStringIndex].add(f);
                });
            });

            previousPitch = openMidi + chosen[chosen.length - 1];
        }

        return result;
    }

    updateSystemPositionOptions() {
        const systemSelect = document.getElementById('SystemSelect');
        if (systemSelect && systemSelect.value !== this.currentSystem) {
            systemSelect.value = this.currentSystem;
        }

        const module = document.getElementById('SystemPositionModule');
        const posSelect = document.getElementById('SystemPositionSelect');
        const cagedSelect = document.getElementById('CAGEDShapeSelect');
        const posLabel = document.getElementById('PositionLabel');
        const npsSelector = document.getElementById('NPSSelector');
        const cagedSelector = document.getElementById('CAGEDSelector');

        if (!module || !posSelect || !cagedSelect || !posLabel) return;

        if (this.currentSystem === 'none') {
            module.style.display = 'none';
            posSelect.style.display = 'none';
            cagedSelect.style.display = 'none';
            if (npsSelector) npsSelector.style.display = 'none';
            if (cagedSelector) cagedSelector.style.display = 'none';
            return;
        }

        module.style.display = 'flex';

        if (this.currentSystem === 'caged') {
            posLabel.textContent = 'ZONE';
            posSelect.style.display = 'none';
            cagedSelect.style.display = 'block';
            if (npsSelector) npsSelector.style.display = 'none';
            if (cagedSelector) cagedSelector.style.display = 'none';
            cagedSelect.value = this.cagedShape;
            document.querySelectorAll('[data-shape]').forEach(b => {
                b.classList.toggle('active', b.dataset.shape === this.cagedShape);
            });
            return;
        }

        const positionCount = this.currentSystem === 'pentatonic' ? 5 : 7;
        const existing = this.systemPositions[this.currentSystem] || this.currentPosition || '1';
        this.currentPosition = existing === 'all' ? 'all' : String(Math.min(positionCount, Math.max(1, parseInt(existing, 10) || 1)));
        this.systemPositions[this.currentSystem] = this.currentPosition;

        const systemLabels = {
            '2nps': '2NPS',
            '3nps': '3NPS',
            '4nps': '4NPS',
            '212': '2-1-2',
            '313': '3-1-3',
            'pentatonic': 'PENT',
            'custom': 'CUSTOM'
        };
        posLabel.textContent = systemLabels[this.currentSystem] || this.currentSystem.toUpperCase();
        cagedSelect.style.display = 'none';
        posSelect.style.display = 'block';
        if (npsSelector) npsSelector.style.display = 'none';
        if (cagedSelector) cagedSelector.style.display = 'none';

        const oldVal = this.currentPosition;
        posSelect.innerHTML = '';
        for (let p = 1; p <= positionCount; p++) {
            const opt = document.createElement('option');
            opt.value = String(p);
            opt.textContent = String(p);
            posSelect.appendChild(opt);
        }
        const allOpt = document.createElement('option');
        allOpt.value = 'all';
        allOpt.textContent = 'ALL';
        posSelect.appendChild(allOpt);
        posSelect.value = oldVal;

        document.querySelectorAll('[data-pos]').forEach(b => {
            b.classList.toggle('active', b.dataset.pos === this.currentPosition);
        });
    }

    // ===== FRETBOARD RENDERING =====
    renderFretboard() {
        const stage = document.getElementById('FretboardSurface');
        if (!stage) return;

        const boardHeightByStrings = { 6: 420, 7: 470, 8: 520 };
        this.height = boardHeightByStrings[this.numStrings] || 420;
        this.marginY = this.numStrings >= 8 ? 52 : 55;
        this.nutX = 78; // open-string gutter (name + open note dot) sits left of the nut

        stage.innerHTML = '';
        // Height comes from the SVG's own aspect ratio — a viewBox-unit
        // minHeight (420 "px") left a dead band under the board at any
        // rendered width, since 2400x420 draws ~0.175x the container width.
        stage.style.minHeight = '0';
        stage.style.minWidth = '1220px';

        const svg = this.createSVG('svg');
        svg.setAttribute('role', 'group');
        svg.setAttribute('aria-label', 'Fretboard notes');
        svg.setAttribute('width', '100%');
        svg.style.aspectRatio = `${this.width} / ${this.height}`;
        svg.style.display = 'block';
        svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
        svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');

        const fx = (x) => this.leftyMode ? this.width - x : x;
        const stringSpacing = (this.height - (this.marginY * 2)) / (this.numStrings - 1);
        const boardTop = this.marginY - Math.min(30, stringSpacing * 0.55);
        const boardBottom = this.height - this.marginY + Math.min(30, stringSpacing * 0.55);
        const boardRight = this.width - 20;

        // Real fret spacing: x(f) = nut + (1 - 2^(-f/12)) / (1 - 2^(-N/12)) * boardLen.
        // The neck's natural taper — wide cells at the nut, tightening toward the
        // bridge — is what makes it read as a guitar at a glance. (The old renderer
        // flattened it with a 0.65 fudge factor.)
        this.fretPositions = [];
        const norm = 1 - Math.pow(2, -this.fretCount / 12);
        for (let f = 0; f <= this.fretCount; f++) {
            const t = (1 - Math.pow(2, -f / 12)) / norm;
            this.fretPositions.push(this.nutX + t * (boardRight - this.nutX));
        }

        // --- Board surface: bounded, warm, square-cornered (system radius-0) ---
        // NOTE: solid paints only in this renderer. SVG gradients with default
        // objectBoundingBox units are undefined on zero-width/height lines, which
        // is exactly why the old frets/nut/inlays/wound strings never rendered.
        const bx = Math.min(fx(this.nutX), fx(boardRight));
        const bw = Math.abs(fx(boardRight) - fx(this.nutX));
        const board = this.createSVG('rect');
        board.setAttribute('x', bx);
        board.setAttribute('y', boardTop);
        board.setAttribute('width', bw);
        board.setAttribute('height', boardBottom - boardTop);
        board.setAttribute('fill', '#1b140e');
        board.setAttribute('stroke', 'rgba(236,226,207,0.10)');
        board.setAttribute('stroke-width', '1');
        svg.appendChild(board);

        const edge = this.createSVG('line');
        edge.setAttribute('x1', bx);
        edge.setAttribute('y1', boardTop + 1.5);
        edge.setAttribute('x2', bx + bw);
        edge.setAttribute('y2', boardTop + 1.5);
        edge.setAttribute('stroke', 'rgba(236,226,207,0.06)');
        edge.setAttribute('stroke-width', '2');
        svg.appendChild(edge);

        // --- Inlays (behind strings): dots at 3/5/7/9/15/17/19/21, doubles at 12/24 ---
        const inlayFill = 'rgba(236,226,207,0.16)';
        [3, 5, 7, 9, 15, 17, 19, 21].filter(f => f <= this.fretCount).forEach(f => {
            const dot = this.createSVG('circle');
            dot.setAttribute('cx', fx((this.fretPositions[f] + this.fretPositions[f - 1]) / 2));
            dot.setAttribute('cy', this.height / 2);
            dot.setAttribute('r', 11);
            dot.setAttribute('fill', inlayFill);
            svg.appendChild(dot);
        });
        [12, 24].filter(f => f <= this.fretCount).forEach(f => {
            const cxx = fx((this.fretPositions[f] + this.fretPositions[f - 1]) / 2);
            [this.height / 2 - stringSpacing * 1.5, this.height / 2 + stringSpacing * 1.5].forEach(cy => {
                const dot = this.createSVG('circle');
                dot.setAttribute('cx', cxx);
                dot.setAttribute('cy', cy);
                dot.setAttribute('r', 11);
                dot.setAttribute('fill', inlayFill);
                svg.appendChild(dot);
            });
        });

        // --- Fret wires ---
        for (let f = 1; f <= this.fretCount; f++) {
            const x = fx(this.fretPositions[f]);

            const shadow = this.createSVG('line');
            shadow.setAttribute('x1', x + 1.5);
            shadow.setAttribute('y1', boardTop);
            shadow.setAttribute('x2', x + 1.5);
            shadow.setAttribute('y2', boardBottom);
            shadow.setAttribute('stroke', 'rgba(0,0,0,0.3)');
            shadow.setAttribute('stroke-width', '2');
            svg.appendChild(shadow);

            const wire = this.createSVG('line');
            wire.setAttribute('x1', x);
            wire.setAttribute('y1', boardTop);
            wire.setAttribute('x2', x);
            wire.setAttribute('y2', boardBottom);
            wire.setAttribute('stroke', '#7a7060');
            wire.setAttribute('stroke-width', f === 12 ? '3' : '2.5');
            wire.setAttribute('opacity', '0.55');
            svg.appendChild(wire);
        }

        // --- Nut: solid bone slab with a shaded board-side edge ---
        const nut = this.createSVG('rect');
        nut.setAttribute('x', fx(this.nutX) - 5);
        nut.setAttribute('y', boardTop - 4);
        nut.setAttribute('width', '10');
        nut.setAttribute('height', (boardBottom - boardTop) + 8);
        nut.setAttribute('fill', '#ece2cf');
        nut.setAttribute('rx', '2');
        svg.appendChild(nut);

        const nutEdge = this.createSVG('line');
        const nex = fx(this.nutX) + (this.leftyMode ? -6 : 6);
        nutEdge.setAttribute('x1', nex);
        nutEdge.setAttribute('y1', boardTop - 4);
        nutEdge.setAttribute('x2', nex);
        nutEdge.setAttribute('y2', boardBottom + 4);
        nutEdge.setAttribute('stroke', 'rgba(0,0,0,0.35)');
        nutEdge.setAttribute('stroke-width', '1.5');
        svg.appendChild(nutEdge);

        // --- Strings: graded thickness, wound = darker body + bright core hint ---
        for (let s = 0; s < this.numStrings; s++) {
            const y = this.marginY + (s * stringSpacing);
            const thickness = 1.1 + (s * 0.42);
            const isWound = s >= this.numStrings - 3;

            const under = this.createSVG('line');
            under.setAttribute('x1', fx(this.nutX));
            under.setAttribute('y1', y + 1);
            under.setAttribute('x2', fx(boardRight));
            under.setAttribute('y2', y + 1);
            under.setAttribute('stroke', 'rgba(0,0,0,0.4)');
            under.setAttribute('stroke-width', thickness);
            svg.appendChild(under);

            const string = this.createSVG('line');
            string.setAttribute('x1', fx(this.nutX));
            string.setAttribute('y1', y);
            string.setAttribute('x2', fx(boardRight));
            string.setAttribute('y2', y);
            string.setAttribute('stroke', isWound ? '#b3a385' : '#e6ddca');
            string.setAttribute('stroke-width', thickness);
            string.setAttribute('opacity', '0.92');
            svg.appendChild(string);

            if (isWound) {
                const core = this.createSVG('line');
                core.setAttribute('x1', fx(this.nutX));
                core.setAttribute('y1', y);
                core.setAttribute('x2', fx(boardRight));
                core.setAttribute('y2', y);
                core.setAttribute('stroke', '#e6ddca');
                core.setAttribute('stroke-width', Math.max(0.6, thickness * 0.3));
                core.setAttribute('opacity', '0.5');
                svg.appendChild(core);
            }

            // Open-string name in the gutter, aligned to its string
            const label = this.createSVG('text');
            label.setAttribute('x', fx(26));
            label.setAttribute('y', y + 7);
            label.setAttribute('text-anchor', 'middle');
            label.setAttribute('font-family', "'Space Mono', monospace");
            label.setAttribute('font-size', '22');
            label.setAttribute('fill', 'rgba(236,226,207,0.55)');
            label.textContent = this.tuning[(this.numStrings - 1) - s];
            svg.appendChild(label);
        }

        // --- Fret numbers at the landmark frets only ---
        [3, 5, 7, 9, 12, 15, 17, 19, 21, 24].filter(f => f <= this.fretCount).forEach(f => {
            const txt = this.createSVG('text');
            txt.setAttribute('x', fx((this.fretPositions[f] + this.fretPositions[f - 1]) / 2));
            txt.setAttribute('y', this.height - 6);
            txt.setAttribute('class', 'fret-number');
            txt.setAttribute('text-anchor', 'middle');
            txt.setAttribute('font-family', "'Space Mono', monospace");
            txt.setAttribute('font-size', '20');
            txt.setAttribute('fill', f % 12 === 0 ? 'rgba(236,226,207,0.55)' : 'rgba(236,226,207,0.35)');
            txt.textContent = f;
            svg.appendChild(txt);
        });

        this.noteGroup = this.createSVG('g');
        this.noteGroup.setAttribute('id', 'NoteGroup');
        svg.appendChild(this.noteGroup);

        this.constellationGroup = this.createSVG('g');
        this.constellationGroup.setAttribute('id', 'ConstellationGroup');
        svg.appendChild(this.constellationGroup);

        stage.appendChild(svg);
        this.renderStringLabels();
    }

    createSVG(tag) {
        return document.createElementNS("http://www.w3.org/2000/svg", tag);
    }

    renderStringLabels() {
        // String names are painted inside the board SVG (open-string gutter),
        // so the legacy HTML chip column is retired — it could never stay
        // aligned with the SVG strings across widths.
        const labels = document.getElementById('StringLabels');
        if (labels) { labels.innerHTML = ''; labels.style.display = 'none'; }
    }

    // ===== NOTE MAPPING =====
    updateNotes() {
        if (!this.noteGroup || !this.constellationGroup) return;
        this.noteGroup.innerHTML = '';
        this.constellationGroup.innerHTML = '';
        this.noteNodes = [];
        this.lastRenderedRootIdx = this.transposePitchClass(this.notes.indexOf(this.root));

        const activeRoot = (this.currentMode === 'interval' && this.intervalFromNote) ? this.intervalFromNote : this.root;
        let rootIdx = this.transposePitchClass(this.notes.indexOf(activeRoot));
        let intervals = this.getIntervalsForCurrentView();
        let baseIntervalsForRender = [...intervals];
        let arpeggioPitchClasses = new Set();
        const allowedArpeggioStrings = this.getAllowedArpeggioStrings();

        this.harmonicTargetPitchClasses = new Set();
        this.harmonicAvoidPitchClasses = new Set();
        let harmonicNextTargetPitchClasses = new Set();

        if (this.currentMode === 'harmonic-context') {
            const harmonicContext = this.getCurrentHarmonicChordContext();
            rootIdx = harmonicContext.rootPc;

            if (this.harmonicDisplayMode === 'chord') intervals = harmonicContext.chordIntervals;
            else if (this.harmonicDisplayMode === 'targets') intervals = harmonicContext.targetIntervals;
            else if (this.harmonicDisplayMode === 'avoids') intervals = harmonicContext.avoidIntervals.length ? harmonicContext.avoidIntervals : harmonicContext.scaleIntervals;
            else intervals = harmonicContext.scaleIntervals;

            intervals = this.applyCustomIntervalAdditions(intervals);
            baseIntervalsForRender = [...intervals];
            this.harmonicTargetPitchClasses = new Set(harmonicContext.targetIntervals.map(i => (rootIdx + i) % 12));
            this.harmonicAvoidPitchClasses = new Set(harmonicContext.avoidIntervals.map(i => (rootIdx + i) % 12));

            if (this.voiceLeadingEnabled) {
                const nextContext = this.getNextHarmonicChordContext();
                if (nextContext) {
                    harmonicNextTargetPitchClasses = new Set(nextContext.targetIntervals.map(i => (nextContext.rootPc + i) % 12));
                }
            }
            this.updateHarmonicReadout();
        }

        if (this.arpeggioEnabled) {
            const arpeggioRoot = this.currentMode === 'harmonic-context' ? rootIdx : this.transposePitchClass(this.notes.indexOf(this.root));
            const arpeggioIntervals = this.getArpeggioIntervals();
            arpeggioPitchClasses = new Set(arpeggioIntervals.map(i => (arpeggioRoot + i) % 12));
            intervals = this.normalizeIntervals([...intervals, ...arpeggioIntervals]);
        }
        this.updateArpeggioReadout();
        this.lastRenderedRootIdx = rootIdx;

        if (this.currentMode === 'chord-tones' || (this.currentSystem === 'caged' && this.cagedSource === 'chord')) {
            const chordTypeForReadout = this.currentMode === 'chord-tones' ? this.chordType : this.cagedChordType;
            const rawChordIntervals = this.getRawChordIntervals(chordTypeForReadout, true);
            this.updateChordReadout(intervals, rootIdx, rawChordIntervals);
        }

        const stringSpacing = (this.height - (this.marginY * 2)) / (this.numStrings - 1);
        const systemPoints = [];

        let patternMap = null;
        if (['2nps', '3nps', '4nps', '212', '313', 'pentatonic', 'custom'].includes(this.currentSystem) && this.currentPosition !== 'all') {
            const mapKey = [
                this.currentSystem,
                this.currentPosition,
                activeRoot,
                this.scaleType,
                this.numStrings,
                this.fretCount,
                intervals.join(','),
                this.pentatonicFlavor,
                this.customSystemFormula,
                this.constraints.fretSpan,
                this.constraints.lockPosition,
                this.constraints.maxShiftPerString
            ].join('|');

            if (!this.cachedPatternMaps[mapKey]) {
                if (this.currentSystem === '212') {
                    patternMap = this.buildGroupedPatternMap([2, 1, 2], intervals, this.currentPosition, activeRoot);
                } else if (this.currentSystem === '313') {
                    patternMap = this.buildGroupedPatternMap([3, 1, 3], intervals, this.currentPosition, activeRoot);
                } else if (this.currentSystem === 'custom') {
                    patternMap = this.buildGroupedPatternMap(this.customSystemPattern, intervals, this.currentPosition, activeRoot);
                } else {
                    const notesPerString = this.currentSystem === '2nps' ? 2 : this.currentSystem === '4nps' ? 4 : 3;
                    patternMap = this.buildNpsPatternMap(notesPerString, intervals, this.currentPosition, activeRoot);
                }
                patternMap = this.applyConstraintsToPatternMap(patternMap);
                this.cachedPatternMaps = { [mapKey]: patternMap };
            } else {
                patternMap = this.cachedPatternMaps[mapKey];
            }
        }

        const baseIntervalsSet = new Set(baseIntervalsForRender);

        for (let s = 0; s < this.numStrings; s++) {
            const tuningIdx = (this.numStrings - 1) - s;
            const openNote = this.tuning[tuningIdx];
            const openNoteVal = this.notes.indexOf(openNote) >= 0 ? this.notes.indexOf(openNote) : 0;
            const stringY = this.marginY + (s * stringSpacing);

            for (let f = 0; f <= this.fretCount; f++) {
                const boardNoteVal = (openNoteVal + this.capo + f) % 12;
                const noteVal = this.transposePitchClass(boardNoteVal);
                let interval = noteVal - rootIdx;
                if (interval < 0) interval += 12;

                if (intervals.includes(interval)) {
                    const isBaseTone = baseIntervalsSet.has(interval);
                    const isArpeggioTone = this.arpeggioEnabled && arpeggioPitchClasses.has(noteVal);

                    if (!isBaseTone && this.arpeggioEnabled && !isArpeggioTone) continue;
                    if (!isBaseTone && isArpeggioTone && allowedArpeggioStrings && !allowedArpeggioStrings.has(s)) continue;

                    let role = 'ext';
                    if (interval === 0) role = 'root';
                    else if ([3, 4].includes(interval)) role = 'third';
                    else if (interval === 7) role = 'fifth';
                    else if ([10, 11].includes(interval)) role = 'seventh';

                    if (this.showingTriads && (role === 'ext' || role === 'seventh')) continue;

                    // System Logic
                    let inSystem = true;
                    if (this.currentSystem === 'caged') {
                        inSystem = this.isInCagedShape(f, s, rootIdx);
                    } else if (['2nps', '3nps', '4nps', '212', '313', 'pentatonic', 'custom'].includes(this.currentSystem)) {
                        inSystem = this.currentPosition === 'all' ? true : !!(patternMap && patternMap[s] && patternMap[s].has(f));
                    }

                    let x = f === 0 ? this.nutX - 24 : (this.fretPositions[f] + this.fretPositions[f - 1]) / 2;
                    if (this.leftyMode) x = this.width - x;

                    const dimmed = !inSystem;
                    // When heatmap overlay is OFF and we have an active system, skip out-of-system notes entirely
                    if (dimmed && !this.heatmapOverlay && this.currentSystem !== 'none') continue;
                    const node = this.createNoteDot(x, stringY, noteVal, interval, role, f, s, inSystem, dimmed, {
                        isTargetTone: this.harmonicTargetPitchClasses.has(noteVal),
                        isAvoidTone: this.harmonicAvoidPitchClasses.has(noteVal),
                        isNextTargetTone: harmonicNextTargetPitchClasses.has(noteVal),
                        isArpeggioTone
                    });

                    if (inSystem) {
                        systemPoints.push({ x, y: stringY, f, s, role, noteVal });
                    }

                    if (node) this.noteNodes.push(node);
                }
            }
        }

        if (this.currentSystem !== 'none' && systemPoints.length > 0) {
            this.renderSystemConstellation(systemPoints);
        }

        if (this.currentMode === 'harmonic-context' && this.voiceLeadingEnabled) {
            this.renderVoiceLeadingGuides();
        }

        this.lastRenderedRootIdx = rootIdx;

        if (this.playheadIsRunning) {
            this.playheadPath = this.buildPlayheadPath();
            if (!this.playheadPath.length) this.stopPlayhead();
        }
    }

    isInCagedShape(f, s, rootIdx) {
        // CAGED Logic: Correct Shape Boundaries based on Root Fret
        // Each shape is defined relative to where the "Root" is on the Low E or A string.

        // Find the "E-Shape" root (matches open E / fret 0, 12) on the 6th string
        const lowestRootTuning = this.tuning[0]; // Usually E
        const lowestRootTuningIdx = this.notes.indexOf(lowestRootTuning);
        let rootFretOnSix = (rootIdx - lowestRootTuningIdx);
        while (rootFretOnSix < 0) rootFretOnSix += 12;

        // Effective Fret relative to the "primary" root instance
        let relFret = f - rootFretOnSix;
        // Normalize for cyclic neck (handle wrapping around nut if needed, though simple offset usually works)
        while (relFret < -5) relFret += 12;
        while (relFret > 16) relFret -= 12; // Focus on the "local" block

        const shapes = this.cagedShape === 'all' ? ['C', 'A', 'G', 'E', 'D'] : [this.cagedShape];

        let inPattern = false;

        shapes.forEach(shape => {
            // Updated CAGED offsets relative to the Root on String 6 (which is "E Shape" basis)
            // E Shape: Root is at offset 0. Span approx -1 to 3.
            // D Shape: Connects to E. Root is on String 4. Span approx 2 to 5.
            // C Shape: Connects to D. Root is on String 5 (A string). Span approx 4 to 8.
            // A Shape: Connects to C. Root is on String 5. Span approx 6 to 9.
            // G Shape: Connects to A. Root is on String 6. Span approx 9 to 12.

            // Using simplified "Fret Span" logic for visualization cleanliness:
            let min, max;
            if (shape === 'E') { min = -1; max = 3; }
            else if (shape === 'D') { min = 1; max = 5; }
            else if (shape === 'C') { min = 4; max = 8; }
            else if (shape === 'A') { min = 6; max = 10; }
            else if (shape === 'G') { min = 9; max = 13; }

            // Check if current fret falls within this shapes relative window
            if (relFret >= min && relFret <= max) inPattern = true;

            // Handle 12th fret wrapping for full board visualization
            let relFretOct = relFret - 12;
            if (relFretOct >= min && relFretOct <= max) inPattern = true;

            let relFretOctUp = relFret + 12;
            if (relFretOctUp >= min && relFretOctUp <= max) inPattern = true;
        });

        return inPattern;
    }

    renderSystemConstellation(points) {
        // Draw faint connecting lines between adjacent strings (single pass to avoid duplicates).
        for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const neighbors = points.filter(p2 => p2.s === p1.s + 1 && Math.abs(p2.f - p1.f) <= 3);
            neighbors.forEach(p2 => {
                const line = this.createSVG('line');
                line.setAttribute('x1', p1.x);
                line.setAttribute('y1', p1.y);
                line.setAttribute('x2', p2.x);
                line.setAttribute('y2', p2.y);
                line.setAttribute('stroke', this.colors[p1.role] || 'rgba(236,226,207,0.1)');
                line.setAttribute('stroke-width', '1');
                line.setAttribute('opacity', '0.15');
                this.constellationGroup.appendChild(line);
            });
        }
    }

    renderVoiceLeadingGuides() {
        const currentTargets = this.noteNodes.filter(n => n.inSystem && !n.dimmed && n.isTargetTone);
        const nextTargets = this.noteNodes.filter(n => n.inSystem && !n.dimmed && n.isNextTargetTone);
        if (!currentTargets.length || !nextTargets.length) return;

        currentTargets.forEach(source => {
            const candidate = nextTargets
                .filter(target => Math.abs(target.string - source.string) <= 1)
                .sort((a, b) => Math.abs(a.fret - source.fret) - Math.abs(b.fret - source.fret))[0];

            if (!candidate) return;

            const line = this.createSVG('line');
            line.setAttribute('x1', source.x);
            line.setAttribute('y1', source.y);
            line.setAttribute('x2', candidate.x);
            line.setAttribute('y2', candidate.y);
            line.setAttribute('class', 'voice-leading-line');
            this.constellationGroup.appendChild(line);
        });
    }

createNoteDot(x, y, noteVal, interval, role, fret, string, inSystem = true, dimmed = false, meta = {}) {
    const group = this.createSVG('g');
    group.setAttribute('class', `note-group ${dimmed ? 'is-dimmed' : ''} ${inSystem ? 'is-in-system' : ''}`);
    group.style.cursor = 'pointer';
    group.setAttribute('role', 'button');
    group.setAttribute('aria-label', `${noteVal}, ${this.getLabel(noteVal, interval)}, fret ${fret}, string ${string + 1}`);
    group.setAttribute('tabindex', !dimmed && !this.noteGroup.querySelector('.note-group[tabindex="0"]') ? '0' : '-1');
    if (dimmed) group.setAttribute('aria-disabled', 'true');

    if (meta.isTargetTone) group.classList.add('is-target-tone');
    if (meta.isAvoidTone) group.classList.add('is-avoid-tone');
    if (meta.isNextTargetTone) group.classList.add('is-next-target');
    if (meta.isArpeggioTone) group.classList.add('is-arpeggio-tone');
    if (inSystem && this.currentSystem === 'caged') group.classList.add('is-caged-active');

    // Color by role
    let glowColor = this.colors[role];
    let glowOpacity = dimmed ? '0.05' : '0.2';

    const glow = this.createSVG('circle');
    glow.setAttribute('cx', x);
    glow.setAttribute('cy', y);
    glow.setAttribute('r', inSystem ? 16 : 13);
    glow.setAttribute('fill', glowColor);
    glow.setAttribute('opacity', dimmed ? '0.05' : '0.4');
    glow.style.filter = 'blur(12px)';
    glow.style.transition = this.reducedMotion ? 'none' : 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
    group.appendChild(glow);

    // Sub-glow for intense core
    if (!dimmed) {
        const coreGlow = this.createSVG('circle');
        coreGlow.setAttribute('cx', x);
        coreGlow.setAttribute('cy', y);
        coreGlow.setAttribute('r', 9);
        coreGlow.setAttribute('fill', '#ece2cf');
        coreGlow.setAttribute('opacity', '0.3');
        coreGlow.style.filter = 'blur(4px)';
        group.appendChild(coreGlow);
    }

    const dot = this.createSVG('circle');
    dot.setAttribute('cx', x);
    dot.setAttribute('cy', y);
    dot.setAttribute('r', 11);
    dot.setAttribute('class', `note-dot note-dot--${role}`);
    dot.setAttribute('fill', glowColor);
    dot.setAttribute('stroke', '#ece2cf');
    dot.setAttribute('stroke-width', '1.5');
    dot.setAttribute('stroke-opacity', dimmed ? '0.1' : '0.4');
    group.appendChild(dot);

    // Shape Indicator (SVG Path)
    if (!dimmed) {
        const shape = this.createSVG('path');
        let pathData = '';

        // Scaled paths for indicators
        switch (role) {
            case 'root':
                // Diamond
                pathData = `M${x},${y - 6} L${x + 6},${y} L${x},${y + 6} L${x - 6},${y} Z`;
                break;
            case 'third':
                // Triangle (Upward)
                pathData = `M${x},${y - 6} L${x + 6},${y + 4} L${x - 6},${y + 4} Z`;
                break;
            case 'fifth':
                // Hexagon
                pathData = `M${x},${y - 6} L${x + 5},${y - 3} L${x + 5},${y + 3} L${x},${y + 6} L${x - 5},${y + 3} L${x - 5},${y - 3} Z`;
                break;
            default:
                // Circle (small inner dot)
                const innerCircle = this.createSVG('circle');
                innerCircle.setAttribute('cx', x);
                innerCircle.setAttribute('cy', y);
                innerCircle.setAttribute('r', 3.5);
                innerCircle.setAttribute('fill', 'rgba(236,226,207,0.4)');
                group.appendChild(innerCircle);
                break;
        }

        if (pathData) {
            shape.setAttribute('d', pathData);
            shape.setAttribute('fill', 'rgba(236,226,207,0.7)');
            shape.setAttribute('style', 'pointer-events: none;');
            group.appendChild(shape);
        }
    }

    const labelText = this.getLabel(noteVal, interval);
    const label = this.createSVG('text');
    label.setAttribute('x', x);
    label.setAttribute('y', y);
    label.setAttribute('dy', '0.36em'); // Measured centering for Inter/Sans
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('class', 'note-label');
    label.style.fontWeight = '900';
    label.style.fontSize = '10.5px';
    label.style.letterSpacing = '-0.03em';
    label.style.fill = role === 'root' ? '#000' : '#fff';
    label.style.pointerEvents = 'none';
    label.setAttribute('opacity', dimmed ? '0.3' : '1');
    label.textContent = labelText;

    group.appendChild(label);

    const showFocusedNote = () => {
        if (dimmed) return;
        glow.setAttribute('opacity', '0.62');
        glow.setAttribute('r', '20');
        dot.setAttribute('r', 12.5);
        dot.setAttribute('stroke-width', '2');
        this.showNoteInfo(noteVal, interval, fret, string);
    };

    const clearFocusedNote = () => {
        if (dimmed) return;
        glow.setAttribute('opacity', '0.4');
        glow.setAttribute('r', inSystem ? 16 : 13);
        dot.setAttribute('r', 11);
        dot.setAttribute('stroke-width', '1.5');
    };

    group.addEventListener('mouseenter', showFocusedNote);
    group.addEventListener('focus', showFocusedNote);
    group.addEventListener('mouseleave', clearFocusedNote);
    group.addEventListener('blur', clearFocusedNote);

    const activateNote = () => {
        this.playNote(noteVal, 4 - Math.floor(string / 2));
        if (this.currentMode === 'ear-training') this.checkAnswer(noteVal);

        // Interaction Feedback
        if (!this.reducedMotion) {
            dot.setAttribute('r', '13');
            setTimeout(() => dot.setAttribute('r', '11'), 100);
        }
    };

    group.addEventListener('click', activateNote);
    group.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activateNote();
            return;
        }
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const notes = Array.from(this.noteGroup.querySelectorAll('.note-group:not(.is-dimmed)'));
        const currentIndex = notes.indexOf(group);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = notes.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + notes.length) % notes.length;
        else nextIndex = (currentIndex + 1) % notes.length;
        notes.forEach((note, index) => note.setAttribute('tabindex', index === nextIndex ? '0' : '-1'));
        notes[nextIndex]?.focus();
    });

    this.noteGroup.appendChild(group);

    return {
        group,
        x,
        y,
        noteVal,
        interval,
        role,
        fret,
        string,
        inSystem,
        dimmed,
        isTargetTone: !!meta.isTargetTone,
        isAvoidTone: !!meta.isAvoidTone,
        isNextTargetTone: !!meta.isNextTargetTone,
        isArpeggioTone: !!meta.isArpeggioTone
    };
}

getLabel(noteVal, interval) {
    switch (this.displayMode) {
        case 'notes': return this.getDisplayNoteName(noteVal, this.lastRenderedRootIdx);
        case 'intervals': return ['R', 'm2', 'M2', 'm3', 'M3', 'P4', 'TT', 'P5', 'm6', 'M6', 'm7', 'M7'][interval % 12];
        case 'degrees': return ['1', 'b2', '2', 'b3', '3', '4', 'b5', '5', 'b6', '6', 'b7', '7'][interval % 12];
        default: return this.getDisplayNoteName(noteVal, this.lastRenderedRootIdx);
    }
}

showNoteInfo(noteVal, interval, fret, string) {
    const readout = document.getElementById('FretStatusMode');
    if (readout) {
        const noteName = this.getDisplayNoteName(noteVal, this.lastRenderedRootIdx);
        const intervalNames = {
            0: 'Root', 1: 'm2', 2: 'M2', 3: 'm3', 4: 'M3',
            5: 'P4', 6: 'TT', 7: 'P5', 8: 'm6',
            9: 'M6', 10: 'm7', 11: 'M7'
        };
        readout.textContent = `${noteName} (${intervalNames[interval]}) - Fret ${fret}`;
    }
}

updateChordReadout(intervals, sourceRoot = this.root, rawIntervals = []) {
    const display = document.getElementById('ChordTonesDisplay');
    if (!display) return;

    const rootIdx = Number.isFinite(sourceRoot) ? this.transposePitchClass(sourceRoot) : this.transposePitchClass(this.notes.indexOf(sourceRoot));
    const noteNames = intervals.map(i => this.getDisplayNoteName((rootIdx + i) % 12, rootIdx));

    const rawSetByPc = new Set(rawIntervals.map(i => ((i % 12) + 12) % 12));
    const readoutIntervals = [...rawIntervals];
    this.customIntervals.forEach(i => {
        if (!rawSetByPc.has(i)) readoutIntervals.push(i);
    });

    const sourceIntervals = readoutIntervals.length ? readoutIntervals : intervals;
    const degreeNames = sourceIntervals.map(i => {
        const deg = ((i % 12) + 12) % 12;
        const base = ['1', 'b2', '2', 'b3', '3', '4', 'b5', '5', 'b6', '6', 'b7', '7'][deg];

        if (i >= 12) {
            if (deg === 1) return 'b9';
            if (deg === 2) return '9';
            if (deg === 3) return '#9';
            if (deg === 5) return '11';
            if (deg === 6) return '#11';
            if (deg === 8) return 'b13';
            if (deg === 9) return '13';
        }
        return base;
    });

    display.innerHTML = `
            <div class="readout-block">
                <span class="readout-label">NOTES</span>
                <span class="readout-value">${noteNames.join(' • ')}</span>
            </div>
            <div class="readout-block">
                <span class="readout-label">INTERVALS</span>
                <span class="readout-value">${degreeNames.join(' • ')}</span>
            </div>
        `;
}

// ===== EAR TRAINING =====
startGame() {
    this.gameScore = 0;
    this.gameStreak = 0;
    this.gameAccuracyCount = 0;
    this.gameTotalNotes = 0;

    const scoreEl = document.getElementById('GameScore');
    const streakEl = document.getElementById('GameStreak');

    if (scoreEl) scoreEl.textContent = '0';
    if (streakEl) streakEl.textContent = '0';

    if (this.gameTimerInterval) {
        clearInterval(this.gameTimerInterval);
        this.gameTimerInterval = null;
    }

    this.newChallenge();
}

newChallenge() {
    const randomNote = this.notes[Math.floor(Math.random() * 12)];
    this.currentChallenge = { note: randomNote, noteVal: this.notes.indexOf(randomNote) };
    const challengeEl = document.getElementById('ChallengeNote');
    if (challengeEl) challengeEl.textContent = randomNote;
    const liveRegion = document.getElementById('FretLiveRegion');
    if (liveRegion) liveRegion.textContent = `Find ${randomNote} on the fretboard`;
}

checkAnswer(noteVal) {
    if (!this.currentChallenge) return;
    this.gameTotalNotes++;
    const scoreEl = document.getElementById('GameScore');
    const streakEl = document.getElementById('GameStreak');
    if (noteVal === this.currentChallenge.noteVal) {
        this.gameScore += 10 + (this.gameStreak * 2);
        this.gameStreak++;
        this.gameAccuracyCount++;
        if (scoreEl) scoreEl.textContent = String(this.gameScore);
        if (streakEl) streakEl.textContent = String(this.gameStreak);
        this.newChallenge();
    } else {
        this.gameStreak = 0;
        if (streakEl) streakEl.textContent = '0';
    }
}

playChallenge() {
    if (this.currentChallenge) {
        this.playNote(this.currentChallenge.noteVal, 4);
    }
}

// ===== AUDIO =====
initAudio() {
    if (!this.ctx) {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
}

playNote(noteVal, octave) {
    this.initAudio();

    const now = this.ctx.currentTime;
    const midi = 60 + noteVal + ((octave - 4) * 12);
    const freq = 440 * Math.pow(2, (midi - 69) / 12);

    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(freq, now);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, now);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3000, now);
    filter.frequency.exponentialRampToValueAtTime(800, now + 0.5);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 1.1);
    osc2.stop(now + 1.1);
}

playScale() {
    if (this.isPlaying) return;
    this.initAudio();
    this.isPlaying = true;

    const rootIdx = this.notes.indexOf(this.root);
    const scaleData = this.scales[this.scaleType];
    const intervals = scaleData.intervals;
    const noteSpacing = 0.25; // seconds between notes
    const startTime = this.ctx.currentTime + 0.05;

    // Schedule all notes using Web Audio lookahead (no bare setTimeout for timing)
    intervals.forEach((semitones, i) => {
        const noteVal = (rootIdx + semitones) % 12;
        const t = startTime + i * noteSpacing;
        this._scheduleNote(noteVal, 4, t);
    });
    // Final octave root
    const finishTime = startTime + intervals.length * noteSpacing;
    this._scheduleNote(rootIdx, 5, finishTime);

    // Use a single timeout only to clear the isPlaying flag after all notes finish
    const totalDuration = (intervals.length + 1) * noteSpacing + 1.15;
    const flagTimer = setTimeout(() => { this.isPlaying = false; }, Math.round(totalDuration * 1000));
    // Store so visibilitychange can cancel it
    this._playScaleTimer = flagTimer;
}

_scheduleNote(noteVal, octave, startTime) {
    if (!this.ctx) return;
    const midi = 60 + noteVal + ((octave - 4) * 12);
    const freq = 440 * Math.pow(2, (midi - 69) / 12);

    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(freq, startTime);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, startTime);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3000, startTime);
    filter.frequency.exponentialRampToValueAtTime(800, startTime + 0.5);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.15, startTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 1.0);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);

    osc1.start(startTime);
    osc2.start(startTime);
    osc1.stop(startTime + 1.1);
    osc2.stop(startTime + 1.1);
}

    setSystem(systemValue) {
    const allowed = new Set(['none', 'caged', '2nps', '3nps', '4nps', '212', '313', 'pentatonic', 'custom']);
    const nextSystem = allowed.has(systemValue) ? systemValue : 'none';
    this.currentSystem = nextSystem;

    if (this.currentSystem !== 'caged') {
        this.currentPosition = this.systemPositions[this.currentSystem] || this.currentPosition || '1';
    }

    document.querySelectorAll('[data-system]').forEach(b => {
        b.classList.toggle('active', b.dataset.system === this.currentSystem);
    });

    this.updateSystemPositionOptions();
    this.updateNotes();
    this.saveSettings();
}

setSystemPosition(positionValue) {
    this.currentPosition = positionValue || '1';
    if (this.currentSystem !== 'caged') {
        this.systemPositions[this.currentSystem] = this.currentPosition;
    }

    document.querySelectorAll('[data-pos]').forEach(b => {
        b.classList.toggle('active', b.dataset.pos === this.currentPosition);
    });

    const posSelect = document.getElementById('SystemPositionSelect');
    if (posSelect && posSelect.value !== this.currentPosition) posSelect.value = this.currentPosition;

    this.updateNotes();
    this.saveSettings();
}

updateCustomIntervalButtons() {
    document.querySelectorAll('[data-add-interval]').forEach(btn => {
        const val = parseInt(btn.dataset.addInterval, 10);
        btn.classList.toggle('active', this.customIntervals.has(val));
    });
}

// ===== EVENT BINDINGS =====
bindEvents() {
    const rootSelect = document.getElementById('RootSelect');
    const scaleSelect = document.getElementById('ScaleSelect');
    const systemSelect = document.getElementById('SystemSelect');
    const posSelect = document.getElementById('SystemPositionSelect');
    const cagedShapeSelect = document.getElementById('CAGEDShapeSelect');
    const cagedSourceSelect = document.getElementById('CAGEDSourceSelect');
    const cagedChordTypeSelect = document.getElementById('CAGEDChordTypeSelect');
    const pentFlavorSelect = document.getElementById('PentatonicFlavorSelect');
    const settingsToggle = document.getElementById('FretSettingsToggle');
    const settingsClose = document.getElementById('FretSettingsClose');
    const settingsDrawer = document.getElementById('FretSettingsDrawer');
    const settingsOverlay = document.getElementById('FretSettingsOverlay');
    const harmonicProgressionSelect = document.getElementById('HarmonicProgressionSelect');
    const harmonicProgressionInlineSelect = document.getElementById('HarmonicProgressionInlineSelect');
    const harmonicStepSelect = document.getElementById('HarmonicChordStepSelect');
    const harmonicInlineStepSelect = document.getElementById('HarmonicChordInlineStepSelect');
    const harmonicDisplayModeSelect = document.getElementById('HarmonicDisplayModeSelect');
    const voiceLeadingToggleBtn = document.getElementById('VoiceLeadingToggleBtn');
    const arpeggioEnableBtn = document.getElementById('ArpeggioEnableBtn');
    const arpeggioTypeSelect = document.getElementById('ArpeggioTypeSelect');
    const arpeggioInversionSelect = document.getElementById('ArpeggioInversionSelect');
    const arpeggioDropSelect = document.getElementById('ArpeggioDropSelect');
    const arpeggioStringSetSelect = document.getElementById('ArpeggioStringSetSelect');
    const customShapeFormulaInput = document.getElementById('CustomShapeFormulaInput');
    const customShapePresetNameInput = document.getElementById('CustomShapePresetNameInput');
    const customShapePresetSelect = document.getElementById('CustomShapePresetSelect');
    const constraintFretSpanInput = document.getElementById('ConstraintFretSpanInput');
    const constraintPositionLockBtn = document.getElementById('ConstraintPositionLockBtn');
    const constraintMaxShiftInput = document.getElementById('ConstraintMaxShiftInput');
    const constraintStringSkipSelect = document.getElementById('ConstraintStringSkipSelect');
    const customTuningInput = document.getElementById('CustomTuningInput');
    const capoSelect = document.getElementById('CapoSelect');
    const transposeSelect = document.getElementById('TransposeSelect');
    const noteSpellingSelect = document.getElementById('NoteSpellingSelect');
    const playheadBpmInput = document.getElementById('PlayheadBpmInput');
    const playheadStartFretInput = document.getElementById('PlayheadStartFretInput');
    const playheadEndFretInput = document.getElementById('PlayheadEndFretInput');
    const validationMicToggleBtn = document.getElementById('ValidationMicToggleBtn');
    const validationMidiToggleBtn = document.getElementById('ValidationMidiToggleBtn');

    [settingsOverlay, settingsDrawer].forEach((element) => {
        if (element && element.parentElement !== document.body) document.body.appendChild(element);
    });

    const getSettingsFocusables = () => Array.from(settingsDrawer?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);

    const trapSettingsFocus = (event) => {
        if (event.key !== 'Tab' || !settingsDrawer?.classList.contains('open')) return;
        const focusables = getSettingsFocusables();
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const setDisplayMode = (display) => {
        this.displayMode = display;
        document.querySelectorAll('[data-display]').forEach(btn => {
            const active = btn.dataset.display === display;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.updateNotes();
    };

    const openSettings = () => {
        settingsDrawer?.classList.add('open');
        settingsOverlay?.classList.add('open');
        settingsToggle?.classList.add('active');
        settingsToggle?.setAttribute('aria-expanded', 'true');
        settingsDrawer?.setAttribute('aria-hidden', 'false');
        settingsDrawer?.removeAttribute('inert');
        settingsOverlay?.setAttribute('aria-hidden', 'false');
        document.body.classList.add('fret-settings-open');
        document.body.setAttribute('data-fretboard-scroll-lock', 'true');
        window.FLToolDialogIsolation?.open(settingsDrawer, [settingsOverlay]);
        requestAnimationFrame(() => {
            try { settingsClose?.focus({ preventScroll: true }); }
            catch (_) { settingsClose?.focus(); }
        });
    };

    const closeSettings = () => {
        const wasOpen = settingsDrawer?.classList.contains('open');
        window.FLToolDialogIsolation?.close(settingsDrawer);
        settingsDrawer?.classList.remove('open');
        settingsOverlay?.classList.remove('open');
        settingsToggle?.classList.remove('active');
        settingsToggle?.setAttribute('aria-expanded', 'false');
        settingsDrawer?.setAttribute('aria-hidden', 'true');
        settingsDrawer?.setAttribute('inert', '');
        settingsOverlay?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('fret-settings-open');
        document.body.removeAttribute('data-fretboard-scroll-lock');
        if (wasOpen) {
            try { settingsToggle?.focus({ preventScroll: true }); }
            catch (_) { settingsToggle?.focus(); }
        }
    };

    const hardUnlockScroll = () => {
        document.body.classList.remove('fret-settings-open');
        document.body.removeAttribute('data-fretboard-scroll-lock');
    };

    // Safety reset for cached body state on browser/page transitions (Safari-friendly).
    hardUnlockScroll();

    const syncCagedControls = () => {
        if (!cagedChordTypeSelect) return;
        const disabled = this.cagedSource !== 'chord';
        cagedChordTypeSelect.disabled = disabled;
        const row = cagedChordTypeSelect.closest('.config-row');
        if (row) row.classList.toggle('is-disabled', disabled);
    };

    const syncHarmonicSelectors = () => {
        if (harmonicProgressionSelect) harmonicProgressionSelect.value = this.harmonicProgressionKey;
        if (harmonicProgressionInlineSelect) harmonicProgressionInlineSelect.value = this.harmonicProgressionKey;
        if (harmonicDisplayModeSelect) harmonicDisplayModeSelect.value = this.harmonicDisplayMode;
        this.populateHarmonicChordStepSelects();
        this.updateHarmonicReadout();
    };

    const syncArpeggioToggle = () => {
        if (!arpeggioEnableBtn) return;
        arpeggioEnableBtn.classList.toggle('active', this.arpeggioEnabled);
        arpeggioEnableBtn.textContent = this.arpeggioEnabled ? 'ON' : 'OFF';
        arpeggioEnableBtn.setAttribute('aria-pressed', String(this.arpeggioEnabled));
    };

    const syncConstraintToggle = () => {
        if (!constraintPositionLockBtn) return;
        constraintPositionLockBtn.classList.toggle('active', this.constraints.lockPosition);
        constraintPositionLockBtn.textContent = this.constraints.lockPosition ? 'ON' : 'OFF';
        constraintPositionLockBtn.setAttribute('aria-pressed', String(this.constraints.lockPosition));
    };

    const setHarmonicProgression = (nextKey) => {
        if (!this.harmonicProgressions[nextKey]) return;
        this.harmonicProgressionKey = nextKey;
        this.harmonicChordIndex = 0;
        syncHarmonicSelectors();
        this.updateNotes();
    };

    const setHarmonicStepIndex = (nextIndex) => {
        const progression = this.harmonicProgressions[this.harmonicProgressionKey];
        if (!progression || !progression.steps.length) return;
        const max = progression.steps.length - 1;
        this.harmonicChordIndex = Math.max(0, Math.min(max, nextIndex));
        this.populateHarmonicChordStepSelects();
        this.updateHarmonicReadout();
        this.updateNotes();
    };

    const cycleHarmonicStep = (delta) => {
        const progression = this.harmonicProgressions[this.harmonicProgressionKey];
        if (!progression || !progression.steps.length) return;
        const len = progression.steps.length;
        const next = ((this.harmonicChordIndex + delta) % len + len) % len;
        setHarmonicStepIndex(next);
    };

    rootSelect?.addEventListener('change', (e) => {
        this.root = e.target.value;
        const chordRootSelect = document.getElementById('ChordRootSelect');
        if (chordRootSelect) chordRootSelect.value = this.root;
        this.cachedPatternMaps = {};
        this.updateNotes();
        this.populateHarmonicChordStepSelects();
        this.updateHarmonicReadout();
        this.updateStatus();
        this.saveSettings();
    });

    scaleSelect?.addEventListener('change', (e) => {
        this.scaleType = e.target.value;
        this.cachedPatternMaps = {};
        this.updateNotes();
        this.updateHarmonicReadout();
        this.updateStatus();
        this.saveSettings();
    });

    document.querySelectorAll('[data-strings]').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('[data-strings]').forEach(b => {
                const active = b === btn;
                b.classList.toggle('active', active);
                b.setAttribute('aria-pressed', String(active));
            });
            this.numStrings = parseInt(btn.dataset.strings, 10);
            this.tuning = this.tunings[this.numStrings];
            this.customTuningMidis = null;
            if (customTuningInput) customTuningInput.value = this.tuning.join(' ');
            this.cachedPatternMaps = {};
            this.populateTuningPresets();
            this.renderFretboard();
            this.updateNotes();
            this.saveSettings();
        });
    });

    document.querySelectorAll('[data-display]').forEach(btn => {
        btn.addEventListener('click', () => {
            setDisplayMode(btn.dataset.display);
            this.saveSettings();
        });
    });

    systemSelect?.addEventListener('change', (e) => {
        this.cachedPatternMaps = {};
        this.setSystem(e.target.value);
    });

    document.querySelectorAll('[data-system]').forEach(btn => {
        btn.addEventListener('click', () => {
            this.cachedPatternMaps = {};
            this.setSystem(btn.dataset.system);
        });
    });

    posSelect?.addEventListener('change', (e) => {
        this.cachedPatternMaps = {};
        this.setSystemPosition(e.target.value);
    });

    document.querySelectorAll('[data-pos]').forEach(btn => {
        btn.addEventListener('click', () => {
            this.cachedPatternMaps = {};
            this.setSystemPosition(btn.dataset.pos);
        });
    });

    cagedShapeSelect?.addEventListener('change', (e) => {
        this.cagedShape = e.target.value;
        document.querySelectorAll('[data-shape]').forEach(b => b.classList.toggle('active', b.dataset.shape === this.cagedShape));
        this.updateNotes();
    });

    document.querySelectorAll('[data-shape]').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('[data-shape]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            this.cagedShape = btn.dataset.shape;
            if (cagedShapeSelect) cagedShapeSelect.value = this.cagedShape;
            this.updateNotes();
        });
    });

    cagedSourceSelect?.addEventListener('change', (e) => {
        this.cagedSource = e.target.value;
        syncCagedControls();
        this.updateNotes();
        this.saveSettings();
    });

    cagedChordTypeSelect?.addEventListener('change', (e) => {
        this.cagedChordType = e.target.value;
        this.updateNotes();
        this.saveSettings();
    });

    pentFlavorSelect?.addEventListener('change', (e) => {
        this.pentatonicFlavor = e.target.value;
        this.cachedPatternMaps = {};
        this.updateNotes();
    });

    harmonicProgressionSelect?.addEventListener('change', (e) => setHarmonicProgression(e.target.value));
    harmonicProgressionInlineSelect?.addEventListener('change', (e) => setHarmonicProgression(e.target.value));
    harmonicStepSelect?.addEventListener('change', (e) => setHarmonicStepIndex(parseInt(e.target.value, 10) || 0));
    harmonicInlineStepSelect?.addEventListener('change', (e) => setHarmonicStepIndex(parseInt(e.target.value, 10) || 0));
    document.getElementById('HarmonicPrevChordBtn')?.addEventListener('click', () => cycleHarmonicStep(-1));
    document.getElementById('HarmonicInlinePrevBtn')?.addEventListener('click', () => cycleHarmonicStep(-1));
    document.getElementById('HarmonicNextChordBtn')?.addEventListener('click', () => cycleHarmonicStep(1));
    document.getElementById('HarmonicInlineNextBtn')?.addEventListener('click', () => cycleHarmonicStep(1));

    harmonicDisplayModeSelect?.addEventListener('change', (e) => {
        this.harmonicDisplayMode = e.target.value;
        this.updateHarmonicReadout();
        this.updateNotes();
    });

    voiceLeadingToggleBtn?.addEventListener('click', () => {
        this.voiceLeadingEnabled = !this.voiceLeadingEnabled;
        voiceLeadingToggleBtn.classList.toggle('active', this.voiceLeadingEnabled);
        voiceLeadingToggleBtn.textContent = this.voiceLeadingEnabled ? 'ON' : 'OFF';
        voiceLeadingToggleBtn.setAttribute('aria-pressed', String(this.voiceLeadingEnabled));
        this.updateHarmonicReadout();
        this.updateNotes();
    });

    arpeggioEnableBtn?.addEventListener('click', () => {
        this.arpeggioEnabled = !this.arpeggioEnabled;
        syncArpeggioToggle();
        this.updateArpeggioReadout();
        this.updateNotes();
    });

    arpeggioTypeSelect?.addEventListener('change', (e) => {
        this.arpeggioType = e.target.value;
        this.updateArpeggioReadout();
        this.updateNotes();
    });
    arpeggioInversionSelect?.addEventListener('change', (e) => {
        this.arpeggioInversion = e.target.value;
        this.updateArpeggioReadout();
        this.updateNotes();
    });
    arpeggioDropSelect?.addEventListener('change', (e) => {
        this.arpeggioDrop = e.target.value;
        this.updateArpeggioReadout();
        this.updateNotes();
    });
    arpeggioStringSetSelect?.addEventListener('change', (e) => {
        this.arpeggioStringSet = e.target.value;
        this.updateArpeggioReadout();
        this.updateNotes();
    });

    document.getElementById('ApplyCustomShapeBtn')?.addEventListener('click', () => {
        const formula = (customShapeFormulaInput?.value || '').trim();
        if (!formula) return;
        if (this.setCustomSystemFormula(formula)) {
            this.setSystem('custom');
            if (systemSelect) systemSelect.value = 'custom';
        }
    });
    customShapeFormulaInput?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const formula = (customShapeFormulaInput.value || '').trim();
        if (!formula) return;
        if (this.setCustomSystemFormula(formula)) {
            this.setSystem('custom');
            if (systemSelect) systemSelect.value = 'custom';
        }
    });

    document.getElementById('SaveCustomShapePresetBtn')?.addEventListener('click', () => {
        const formula = (customShapeFormulaInput?.value || '').trim();
        if (!this.setCustomSystemFormula(formula, { silent: true })) return;
        const name = (customShapePresetNameInput?.value || '').trim() || formula;
        this.customShapePresets[name] = this.customSystemFormula;
        this.saveCustomShapePresets();
        this.populateCustomShapePresetSelect();
        const status = document.getElementById('CustomShapeStatus');
        if (status) status.textContent = `Saved preset "${name}" (${this.customSystemFormula}).`;
    });

    document.getElementById('LoadCustomShapePresetBtn')?.addEventListener('click', () => {
        const formula = customShapePresetSelect?.value || '';
        if (!formula) return;
        if (customShapeFormulaInput) customShapeFormulaInput.value = formula;
        if (this.setCustomSystemFormula(formula)) {
            this.setSystem('custom');
            if (systemSelect) systemSelect.value = 'custom';
        }
    });

    document.getElementById('ShareCustomShapeBtn')?.addEventListener('click', async () => {
        const formula = (customShapeFormulaInput?.value || this.customSystemFormula || '').trim();
        if (!formula) return;
        const status = document.getElementById('CustomShapeStatus');
        try {
            await navigator.clipboard.writeText(`Fretboard custom formula: ${formula}`);
            if (status) status.textContent = `Copied formula "${formula}" to clipboard.`;
        } catch {
            if (status) status.textContent = `Share formula: ${formula}`;
        }
    });

    constraintFretSpanInput?.addEventListener('input', (e) => {
        this.constraints.fretSpan = Math.max(3, Math.min(this.fretCount, parseInt(e.target.value, 10) || this.fretCount));
        this.cachedPatternMaps = {};
        this.updateNotes();
    });
    constraintMaxShiftInput?.addEventListener('input', (e) => {
        this.constraints.maxShiftPerString = Math.max(0, Math.min(this.fretCount, parseInt(e.target.value, 10) || this.fretCount));
        this.cachedPatternMaps = {};
        this.updateNotes();
    });
    constraintStringSkipSelect?.addEventListener('change', (e) => {
        this.constraints.stringSkip = e.target.value;
        this.updateNotes();
    });
    constraintPositionLockBtn?.addEventListener('click', () => {
        this.constraints.lockPosition = !this.constraints.lockPosition;
        syncConstraintToggle();
        this.cachedPatternMaps = {};
        this.updateNotes();
    });

    document.getElementById('TuningPresetSelect')?.addEventListener('change', (e) => {
        const name = e.target.value;
        const notes = this.tuningPresets[name];
        if (!notes) return; // Custom — let the user type in the drawer
        this.tuning = notes.slice(0, this.numStrings);
        this.customTuningMidis = this.buildCustomTuningMidis(this.tuning);
        const tuningInput = document.getElementById('CustomTuningInput');
        if (tuningInput) tuningInput.value = this.tuning.join(' ');
        this.cachedPatternMaps = {};
        this.renderFretboard();
        this.updateNotes();
        this.saveSettings();
    });

    document.getElementById('ApplyCustomTuningBtn')?.addEventListener('click', () => {
        this.applyCustomTuning(customTuningInput?.value || '');
        // After applying custom, update preset select to Custom
        const presetSelect = document.getElementById('TuningPresetSelect');
        if (presetSelect) presetSelect.value = 'Custom';
    });
    document.getElementById('ResetCustomTuningBtn')?.addEventListener('click', () => {
        this.resetTuning();
        this.populateTuningPresets();
        this.saveSettings();
    });

    capoSelect?.addEventListener('change', (e) => {
        this.capo = Math.max(0, Math.min(12, parseInt(e.target.value, 10) || 0));
        this.updateNotes();
        this.saveSettings();
    });
    transposeSelect?.addEventListener('change', (e) => {
        this.transposeSemitones = Math.max(-12, Math.min(12, parseInt(e.target.value, 10) || 0));
        this.cachedPatternMaps = {};
        this.updateNotes();
        this.saveSettings();
    });
    noteSpellingSelect?.addEventListener('change', (e) => {
        this.noteSpellingMode = e.target.value;
        this.updateNotes();
        this.saveSettings();
    });

    playheadBpmInput?.addEventListener('input', (e) => {
        this.playheadBpm = Math.max(30, Math.min(260, parseInt(e.target.value, 10) || 90));
        if (this.playheadIsRunning) this.startPlayhead();
    });
    playheadStartFretInput?.addEventListener('input', (e) => {
        this.playheadLoopStart = Math.max(0, Math.min(this.fretCount, parseInt(e.target.value, 10) || 0));
        if (this.playheadLoopStart > this.playheadLoopEnd) {
            this.playheadLoopEnd = this.playheadLoopStart;
            if (playheadEndFretInput) playheadEndFretInput.value = String(this.playheadLoopEnd);
        }
    });
    playheadEndFretInput?.addEventListener('input', (e) => {
        this.playheadLoopEnd = Math.max(0, Math.min(this.fretCount, parseInt(e.target.value, 10) || this.fretCount));
        if (this.playheadLoopEnd < this.playheadLoopStart) {
            this.playheadLoopStart = this.playheadLoopEnd;
            if (playheadStartFretInput) playheadStartFretInput.value = String(this.playheadLoopStart);
        }
    });
    document.getElementById('PlayheadStartBtn')?.addEventListener('click', () => this.startPlayhead());
    document.getElementById('PlayheadStopBtn')?.addEventListener('click', () => this.stopPlayhead());

    validationMicToggleBtn?.addEventListener('click', async () => {
        this.validationMicEnabled = !this.validationMicEnabled;
        validationMicToggleBtn.classList.toggle('active', this.validationMicEnabled);
        validationMicToggleBtn.textContent = this.validationMicEnabled ? 'ON' : 'OFF';
        validationMicToggleBtn.setAttribute('aria-pressed', String(this.validationMicEnabled));
        const readout = document.getElementById('ValidationReadout');
        if (this.validationMicEnabled && !this.isListening) {
            const enabled = await this.startListening();
            if (!enabled) {
                this.validationMicEnabled = false;
                validationMicToggleBtn.classList.remove('active');
                validationMicToggleBtn.textContent = 'OFF';
                validationMicToggleBtn.setAttribute('aria-pressed', 'false');
                if (readout) readout.textContent = 'Microphone permission denied.';
            }
            if (enabled && readout) readout.textContent = 'Microphone validation active.';
        } else if (!this.validationMicEnabled && readout) {
            readout.textContent = 'Microphone validation stopped.';
        }
    });

    validationMidiToggleBtn?.addEventListener('click', async () => {
        if (this.validationMidiEnabled) {
            this.stopMidiValidation();
        } else {
            await this.startMidiValidation();
        }
        validationMidiToggleBtn.classList.toggle('active', this.validationMidiEnabled);
        validationMidiToggleBtn.textContent = this.validationMidiEnabled ? 'ON' : 'OFF';
        validationMidiToggleBtn.setAttribute('aria-pressed', String(this.validationMidiEnabled));
    });

    document.getElementById('SaveWorkspaceABtn')?.addEventListener('click', () => {
        const state = this.collectWorkspaceState();
        this.workspaceA = state;
        this.saveWorkspace('A', state);
        const readout = document.getElementById('WorkspaceCompareReadout');
        if (readout) readout.textContent = 'Workspace A saved.';
    });
    document.getElementById('SaveWorkspaceBBtn')?.addEventListener('click', () => {
        const state = this.collectWorkspaceState();
        this.workspaceB = state;
        this.saveWorkspace('B', state);
        const readout = document.getElementById('WorkspaceCompareReadout');
        if (readout) readout.textContent = 'Workspace B saved.';
    });
    document.getElementById('LoadWorkspaceABtn')?.addEventListener('click', () => {
        const next = this.loadWorkspace('A');
        if (!next) return;
        this.workspaceA = next;
        this.applyWorkspaceState(next);
    });
    document.getElementById('LoadWorkspaceBBtn')?.addEventListener('click', () => {
        const next = this.loadWorkspace('B');
        if (!next) return;
        this.workspaceB = next;
        this.applyWorkspaceState(next);
    });
    document.getElementById('CompareWorkspaceBtn')?.addEventListener('click', () => {
        const a = this.workspaceA || this.loadWorkspace('A');
        const b = this.workspaceB || this.loadWorkspace('B');
        const readout = document.getElementById('WorkspaceCompareReadout');
        if (readout) readout.textContent = this.compareWorkspaceStates(a, b);
    });

    settingsToggle?.addEventListener('click', () => {
        if (settingsDrawer?.classList.contains('open')) closeSettings();
        else openSettings();
    });
    settingsClose?.addEventListener('click', closeSettings);
    settingsOverlay?.addEventListener('click', closeSettings);
    document.addEventListener('keydown', (event) => {
        if (!settingsDrawer?.classList.contains('open')) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSettings();
            return;
        }
        trapSettingsFocus(event);
    });
    window.addEventListener('pagehide', hardUnlockScroll, { passive: true });
    window.addEventListener('beforeunload', hardUnlockScroll, { passive: true });
    window.addEventListener('pagehide', () => {
        this.stopPlayhead();
        this.stopMidiValidation();
        if (this.isListening) this.stopListening();
    }, { passive: true });

    document.querySelectorAll('[data-mode]').forEach(tab => {
        tab.setAttribute('aria-pressed', tab.classList.contains('active') ? 'true' : 'false');
        tab.addEventListener('click', () => {
            document.querySelectorAll('[data-mode]').forEach(t => {
                t.classList.remove('active');
                t.setAttribute('aria-pressed', 'false');
            });
            tab.classList.add('active');
            tab.setAttribute('aria-pressed', 'true');
            this.currentMode = tab.dataset.mode;
            this.showModePanel();
            this.updateStatus();
        });
    });

    document.getElementById('TriadToggle')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        btn.classList.toggle('active');
        this.showingTriads = btn.classList.contains('active');
        btn.setAttribute('aria-pressed', this.showingTriads ? 'true' : 'false');
        const status = btn.querySelector('.overlay-btn-status');
        if (status) status.textContent = this.showingTriads ? 'ON' : 'OFF';
        this.updateNotes();
    });

    document.getElementById('HeatmapToggle')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        btn.classList.toggle('active');
        this.heatmapOverlay = btn.classList.contains('active');
        btn.setAttribute('aria-pressed', this.heatmapOverlay ? 'true' : 'false');
        const status = btn.querySelector('.overlay-btn-status');
        if (status) status.textContent = this.heatmapOverlay ? 'ON' : 'OFF';
        this.updateNotes();
    });

    document.getElementById('ClearIntervalAddsBtn')?.addEventListener('click', () => {
        this.customIntervals.clear();
        this.updateCustomIntervalButtons();
        this.updateNotes();
    });

    document.querySelectorAll('[data-add-interval]').forEach(btn => {
        btn.addEventListener('click', () => {
            const interval = parseInt(btn.dataset.addInterval, 10);
            if (this.customIntervals.has(interval)) this.customIntervals.delete(interval);
            else this.customIntervals.add(interval);
            this.updateCustomIntervalButtons();
            this.updateNotes();
        });
    });

    document.getElementById('LeftyToggle')?.addEventListener('click', (e) => {
        this.leftyMode = !this.leftyMode;
        e.currentTarget.textContent = this.leftyMode ? 'LEFT-HANDED' : 'RIGHT-HANDED';
        e.currentTarget.classList.toggle('active', this.leftyMode);
        e.currentTarget.setAttribute('aria-pressed', String(this.leftyMode));
        this.renderFretboard();
        this.updateNotes();
        this.saveSettings();
    });

    document.getElementById('AudioToggle')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (this.isListening) {
            this.stopListening();
            btn.textContent = 'ENABLE MIC';
            btn.classList.remove('active');
        } else {
            const success = await this.startListening();
            if (success) {
                btn.textContent = 'MIC ACTIVE';
                btn.classList.add('active');
            }
        }
    });

    document.querySelectorAll('.int-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.int-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            this.selectedInterval = parseInt(btn.dataset.interval, 10);
            this.updateNotes();
        });
    });

    document.getElementById('IntervalFromNote')?.addEventListener('change', (e) => {
        this.intervalFromNote = e.target.value;
        if (this.currentMode === 'interval') this.updateNotes();
    });

    document.getElementById('ChordRootSelect')?.addEventListener('change', (e) => {
        this.chordRoot = e.target.value;
        this.root = this.chordRoot;
        if (rootSelect) rootSelect.value = this.root;
        this.cachedPatternMaps = {};
        this.updateNotes();
    });

    document.getElementById('ChordTypeSelect')?.addEventListener('change', (e) => {
        this.chordType = e.target.value;
        this.updateNotes();
    });

    const syncExtButtons = (ext, active) => {
        document.querySelectorAll('.ext-toggle').forEach(b => {
            if (b.dataset.ext === ext) {
                b.classList.toggle('active', active);
                b.setAttribute('aria-pressed', active ? 'true' : 'false');
            }
        });
    };

    document.querySelectorAll('.ext-toggle').forEach(btn => {
        btn.setAttribute('aria-pressed', this.selectedExtensions.has(btn.dataset.ext) ? 'true' : 'false');
        btn.addEventListener('click', () => {
            const ext = btn.dataset.ext;
            const next = !this.selectedExtensions.has(ext);
            if (next) this.selectedExtensions.add(ext);
            else this.selectedExtensions.delete(ext);
            syncExtButtons(ext, next);
            this.updateNotes();
        });
    });

    document.getElementById('NewChallengeBtn')?.addEventListener('click', () => {
        if (!this.currentChallenge) this.startGame();
        else this.newChallenge();
    });
    document.getElementById('PlayNoteBtn')?.addEventListener('click', () => this.playChallenge());
    document.getElementById('ShowHintBtn')?.addEventListener('click', () => {
        if (!this.currentChallenge) return;
        const noteVal = this.currentChallenge.noteVal;
        // Flash all matching note dots on the board
        const matches = this.noteNodes.filter(n => n.noteVal === noteVal && n.inSystem && !n.dimmed);
        matches.forEach(n => {
            const dot = n.group?.querySelector('.note-dot');
            if (!dot) return;
            const origR = dot.getAttribute('r');
            dot.setAttribute('r', '14');
            setTimeout(() => dot.setAttribute('r', origR || '11'), 400);
        });
        const liveRegion = document.getElementById('FretLiveRegion');
        if (liveRegion) liveRegion.textContent = `Hint: ${matches.length} location${matches.length !== 1 ? 's' : ''} highlighted`;
    });

    document.addEventListener('keydown', (e) => {
        if (settingsDrawer?.classList.contains('open')) return;
        if (e.target.closest?.('input, select, textarea, button, a, [contenteditable="true"], [role="button"]')) return;

        switch (e.code) {
            case 'Space':
                e.preventDefault();
                if (this.currentMode === 'ear-training') this.newChallenge();
                break;
            case 'KeyP':
                this.playChallenge();
                break;
            case 'KeyN':
                setDisplayMode('notes');
                break;
            case 'KeyD':
                setDisplayMode('degrees');
                break;
            case 'KeyI':
                setDisplayMode('intervals');
                break;
            case 'KeyL':
                this.leftyMode = !this.leftyMode;
                const leftyBtn = document.getElementById('LeftyToggle');
                if (leftyBtn) {
                    leftyBtn.textContent = this.leftyMode ? 'LEFT-HANDED' : 'RIGHT-HANDED';
                    leftyBtn.classList.toggle('active', this.leftyMode);
                    leftyBtn.setAttribute('aria-pressed', String(this.leftyMode));
                }
                this.renderFretboard();
                this.updateNotes();
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                e.preventDefault();
                if (!rootSelect) break;
                if (e.code === 'ArrowUp') {
                    rootSelect.selectedIndex = (rootSelect.selectedIndex - 1 + rootSelect.options.length) % rootSelect.options.length;
                } else {
                    rootSelect.selectedIndex = (rootSelect.selectedIndex + 1) % rootSelect.options.length;
                }
                this.root = rootSelect.value;
                this.cachedPatternMaps = {};
                this.updateNotes();
                this.updateStatus();
                break;
            case 'KeyC':
                if (!e.ctrlKey && !e.metaKey) this.selectCAGEDShape('C');
                break;
            case 'KeyA':
                if (!e.ctrlKey && !e.metaKey) this.selectCAGEDShape('A');
                break;
            case 'KeyG':
                if (!e.ctrlKey && !e.metaKey) this.selectCAGEDShape('G');
                break;
            case 'KeyE':
                if (!e.ctrlKey && !e.metaKey) this.selectCAGEDShape('E');
                break;
        }
    });

    this.updateSystemPositionOptions();
    this.updateCustomIntervalButtons();
    if (systemSelect) systemSelect.value = this.currentSystem;
    if (cagedSourceSelect) cagedSourceSelect.value = this.cagedSource;
    if (cagedChordTypeSelect) cagedChordTypeSelect.value = this.cagedChordType;
    if (pentFlavorSelect) pentFlavorSelect.value = this.pentatonicFlavor;
    if (arpeggioTypeSelect) arpeggioTypeSelect.value = this.arpeggioType;
    if (arpeggioInversionSelect) arpeggioInversionSelect.value = this.arpeggioInversion;
    if (arpeggioDropSelect) arpeggioDropSelect.value = this.arpeggioDrop;
    if (arpeggioStringSetSelect) arpeggioStringSetSelect.value = this.arpeggioStringSet;
    if (capoSelect) capoSelect.value = String(this.capo);
    if (transposeSelect) transposeSelect.value = String(this.transposeSemitones);
    if (noteSpellingSelect) noteSpellingSelect.value = this.noteSpellingMode;
    if (constraintFretSpanInput) constraintFretSpanInput.value = String(this.constraints.fretSpan);
    if (constraintMaxShiftInput) constraintMaxShiftInput.value = String(this.constraints.maxShiftPerString);
    if (constraintStringSkipSelect) constraintStringSkipSelect.value = this.constraints.stringSkip;
    if (customShapeFormulaInput) customShapeFormulaInput.value = this.customSystemFormula;
    if (customTuningInput) customTuningInput.value = this.tuning.join(' ');
    if (playheadBpmInput) playheadBpmInput.value = String(this.playheadBpm);
    if (playheadStartFretInput) playheadStartFretInput.value = String(this.playheadLoopStart);
    if (playheadEndFretInput) playheadEndFretInput.value = String(this.playheadLoopEnd);
    this.populateCustomShapePresetSelect();
    this.updateArpeggioReadout();
    syncArpeggioToggle();
    syncConstraintToggle();
    syncHarmonicSelectors();
    if (validationMicToggleBtn) {
        validationMicToggleBtn.classList.toggle('active', this.validationMicEnabled);
        validationMicToggleBtn.textContent = this.validationMicEnabled ? 'ON' : 'OFF';
        validationMicToggleBtn.setAttribute('aria-pressed', String(this.validationMicEnabled));
    }
    if (validationMidiToggleBtn) {
        validationMidiToggleBtn.classList.toggle('active', this.validationMidiEnabled);
        validationMidiToggleBtn.textContent = this.validationMidiEnabled ? 'ON' : 'OFF';
        validationMidiToggleBtn.setAttribute('aria-pressed', String(this.validationMidiEnabled));
    }
    syncCagedControls();
}

selectCAGEDShape(shape) {
    if (this.currentSystem !== 'caged') {
        this.setSystem('caged');
    }

    this.cagedShape = shape;
    document.querySelectorAll('[data-shape]').forEach(b => {
        b.classList.toggle('active', b.dataset.shape === shape);
    });
    const cagedShapeSelect = document.getElementById('CAGEDShapeSelect');
    if (cagedShapeSelect) cagedShapeSelect.value = shape;
    this.updateNotes();
}

    async startListening() {
    if (this.isListening) return true;
    try {
        this.initAudio();
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });

        // In production, we'd use a local pitch-processor.js. For now, we mock if needed or assume existence.
        // Using a simple analyzer fallback if worklet fails
        this.analyser = this.ctx.createAnalyser();
        this.analyser.fftSize = 2048;
        this.source = this.ctx.createMediaStreamSource(this.mediaStream);
        this.source.connect(this.analyser);

        this.isListening = true;
        const statusInd = document.getElementById('AudioStatusIndicator');
        if (statusInd) {
            statusInd.textContent = 'LISTENING...';
            statusInd.classList.add('active');
        }

        this.detectionLoop();
        return true;
    } catch (err) {
        console.error('Mic access denied', err);
        return false;
    }
}

stopListening() {
    this.isListening = false;
    try { this.source?.disconnect(); } catch (_) {}
    this.source = null;
    this.analyser = null;
    if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
    }
    this.mediaStream = null;
    if (this.validationMicEnabled) {
        this.validationMicEnabled = false;
        const validationMicToggleBtn = document.getElementById('ValidationMicToggleBtn');
        if (validationMicToggleBtn) {
            validationMicToggleBtn.classList.remove('active');
            validationMicToggleBtn.textContent = 'OFF';
        }
    }
    const statusInd = document.getElementById('AudioStatusIndicator');
    if (statusInd) {
        statusInd.textContent = 'MIC INACTIVE';
        statusInd.classList.remove('active');
    }
    const readout = document.getElementById('ValidationReadout');
    if (readout && !this.validationMicEnabled) {
        readout.textContent = 'Microphone validation stopped.';
    }
}

detectionLoop() {
    if (!this.isListening) return;

    if (this.validationMicEnabled && this.analyser && this.ctx) {
        const buffer = new Float32Array(this.analyser.fftSize);
        this.analyser.getFloatTimeDomainData(buffer);
        const frequency = this.autoCorrelate(buffer, this.ctx.sampleRate);
        if (frequency > 0) {
            const midi = Math.round(69 + (12 * Math.log2(frequency / 440)));
            const pitchClass = ((midi % 12) + 12) % 12;
            const now = performance.now();
            if (this.lastDetectedNote !== pitchClass || (now - this.lastDetectionAt) > 220) {
                this.lastDetectedNote = pitchClass;
                this.lastDetectionAt = now;
                this.validateInputNote(pitchClass, 'mic');
            }
        }
    }

    requestAnimationFrame(() => this.detectionLoop());
}

updateStatus() {
    const statusMode = document.getElementById('FretStatusMode');
    if (statusMode) {
        const labels = {
            visualizer: 'READY',
            'ear-training': 'EAR TRAINING',
            interval: 'INTERVAL',
            'chord-tones': 'CHORD TONES',
            'harmonic-context': 'HARMONIC'
        };
        statusMode.textContent = labels[this.currentMode] || String(this.currentMode || 'READY').replace(/-/g, ' ').toUpperCase();
    }
}

showModePanel() {
    const panels = ['EarTrainingPanel', 'IntervalPanel', 'ChordTonesPanel', 'HarmonicContextPanel'];
    panels.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
    });

    if (this.currentMode !== 'ear-training' && this.gameTimerInterval) {
        clearInterval(this.gameTimerInterval);
        this.gameTimerInterval = null;
    }

    if (this.currentMode === 'ear-training') {
        document.getElementById('EarTrainingPanel').style.display = 'block';
        this.startGame();
    } else if (this.currentMode === 'interval') {
        document.getElementById('IntervalPanel').style.display = 'block';
    } else if (this.currentMode === 'chord-tones') {
        document.getElementById('ChordTonesPanel').style.display = 'block';
    } else if (this.currentMode === 'harmonic-context') {
        document.getElementById('HarmonicContextPanel').style.display = 'block';
        this.updateHarmonicReadout();
    }

    this.updateNotes();
}
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.flRunWhenPrivacyReady(() => {
        const engine = new FretboardEngineV20();
        engine.init();
        window.fretboardApp = engine;
    });
});
